import { formatDisplayDateTime } from "../../utils/dateUtils";
﻿import { ColumnDef } from "@tanstack/react-table";
import { AxiosError } from "axios";
import React, { useEffect, useState, useRef } from "react";
import { LoginAxiosInstance } from "../../Network/LoginAxiosInstance";
import DataTable2 from "shared/DataTable2";
import UnifiedSearchBox from "../../shared/UnifiedSearchBox";
import ServerDeletedPills from "shared/ServerDeletedPills";
import { ServerDeletedFilterValue } from "shared/ServerListFilters";
import { useServerPaginatedList } from "shared/useServerPaginatedList";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/Label";
import { Toaster } from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import { Option } from "../../interface/Option";
import { createApiClient } from "../../Network/ApiClient";
import {
  activityNotAllowedIncludeActivity,
  Datum,
} from "../../interface/ActivityNotAllowedIncludeActivity";
import ListofActivityCode from "../Shared/ListOfActivityCode";
import ListOfActivityCustom from "../Shared/ListOfActivityCustom";
import ListOfActivityMedical from "../Shared/ListOfActivityMedical";
import ListOfNewNetworkName from "../Shared/NewNetworkName";
import ListOfInsuranceplanName from "../Shared/ListOfInsuranceplanName";
import VipPatientSelector from "Pages/Shared/VipPatient";
import ListOfProviderName from "../Shared/ListOfProviderName";
import PolicyNames from "../Shared/PolicyNames";
import FileUpload from "Pages/uploadFile/ActivityNotAllowedIncludeActivity/ActivityNotAllowedIncludeActivity";
import ListOfDoctorName from "../Shared/Doctor";
import { DoctorAxiosInstance } from "../../Network/DoctorAxiosInstance";
import Vip from "../Shared/Vip";
import ListOfSpecialty from "../Shared/Specialty";

const ActivityBundleAxiosInstance = createApiClient("ActivityBundle/");

// ─── Helpers: role ────────────────────────────────────────────────────────────
const getUserRole = (): string => {
  try {
    const token = localStorage.getItem("token") || "";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.userType || "";
  } catch { return ""; }
};

// ─── Types ────────────────────────────────────────────────────────────────────
export interface PolicyItem {
  id?: number;
  Policy_id: string;
  Policy_name: string;
  plans?: Array<{
    Plan_name?: string;
    plan_name?: string;
    name?: string;
    id?: number;
    [key: string]: any;
  }>;
}

interface CodeWithType {
  code: string | number;
  type: string;
}

interface OrderedActivityItem {
  type: "code" | "customgroup" | "medicalgroup";
  code?: string | number;
  activity_type?: string;
  id?: string | number;
  name?: string;
  side: "not_allowed";
  activity_order: number;
}

type ActivitiesByType = { CPT: Option[]; HCPCS: Option[]; Dental: Option[]; Drugs: Option[] };
const EMPTY_BY_TYPE: ActivitiesByType = { CPT: [], HCPCS: [], Dental: [], Drugs: [] };

interface ActivitySelectionGroup {
  id: string;
  selectedOption: string | null;
  activitiesByType: ActivitiesByType;
  medicalGroups: Option[];
  customGroups: Option[];
  isExpanded: boolean;
}

const createActivitySelectionGroup = (
  overrides: Partial<ActivitySelectionGroup> = {},
): ActivitySelectionGroup => ({
  id: `activity-group-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  selectedOption: null,
  activitiesByType: { CPT: [], HCPCS: [], Dental: [], Drugs: [] },
  medicalGroups: [],
  customGroups: [],
  isExpanded: true,
  ...overrides,
});

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getActivityTypeColor = (type: string) => {
  const colors: Record<string, { bg: string; border: string; text: string }> = {
    CPT:    { bg: "bg-blue-100",   border: "border-blue-500",   text: "text-blue-900"   },
    HCPCS:  { bg: "bg-green-100",  border: "border-green-500",  text: "text-green-900"  },
    Dental: { bg: "bg-purple-100", border: "border-purple-500", text: "text-purple-900" },
    Drugs:  { bg: "bg-orange-100", border: "border-orange-500", text: "text-orange-900" },
  };
  return colors[type] || { bg: "bg-gray-100", border: "border-gray-500", text: "text-gray-900" };
};

const flattenByType = (byType: ActivitiesByType): CodeWithType[] => {
  const result: CodeWithType[] = [];
  Object.entries(byType).forEach(([type, options]) => {
    options.forEach((o) => result.push({ code: o.value as string, type }));
  });
  return result;
};

const normalizeActivityType = (type: string | undefined): keyof ActivitiesByType => {
  const normalized = (type || "CPT").toLowerCase();
  if (normalized === "hcpcs") return "HCPCS";
  if (normalized === "dental") return "Dental";
  if (normalized === "drugs") return "Drugs";
  return "CPT";
};

const buildItemsOrder = (groups: ActivitySelectionGroup[]): OrderedActivityItem[] =>
  groups.flatMap((group, groupIndex) => {
    const activityOrder = groupIndex + 1;
    const codes: OrderedActivityItem[] = flattenByType(group.activitiesByType).map((item) => ({
      type: "code",
      code: item.code,
      activity_type: item.type.toLowerCase(),
      side: "not_allowed",
      activity_order: activityOrder,
    }));
    const medical: OrderedActivityItem[] = group.medicalGroups.map((item) => ({
      type: "medicalgroup",
      id: item.value as string | number,
      name: String(item.label ?? ""),
      side: "not_allowed",
      activity_order: activityOrder,
    }));
    const custom: OrderedActivityItem[] = group.customGroups.map((item) => ({
      type: "customgroup",
      id: item.value as string | number,
      name: String(item.label ?? ""),
      side: "not_allowed",
      activity_order: activityOrder,
    }));
    return [...codes, ...medical, ...custom];
  });

const orderedItemKey = (item: OrderedActivityItem): string => {
  if (item.type === "code") {
    return `code:${String(item.activity_type || "").toLowerCase()}:${String(item.code)}`;
  }
  return `${item.type}:${String(item.id)}`;
};

const getOrderedItems = (item: any): OrderedActivityItem[] =>
  Array.isArray(item?.items_order)
    ? item.items_order
        .map((entry: any, index: number) => ({
          ...entry,
          side: "not_allowed" as const,
          activity_order: Number(entry.activity_order ?? entry.group_order ?? index + 1),
        }))
        .sort((a: OrderedActivityItem, b: OrderedActivityItem) => a.activity_order - b.activity_order)
    : [];

const groupByType = (items: CodeWithType[]): ActivitiesByType => {
  const grouped: ActivitiesByType = { CPT: [], HCPCS: [], Dental: [], Drugs: [] };
  const typeMap: Record<string, keyof ActivitiesByType> = {
    cpt: "CPT", CPT: "CPT",
    hcpcs: "HCPCS", HCPCS: "HCPCS",
    dental: "Dental", Dental: "Dental",
    drugs: "Drugs", Drugs: "Drugs",
  };
  items.forEach(({ code, type }) => {
    const key = typeMap[type];
    if (key) grouped[key].push({ value: code, label: code.toString() });
  });
  return grouped;
};


const firstText = (...values: any[]): string => {
  for (const value of values) {
    if (
      value !== null &&
      value !== undefined &&
      typeof value !== "object"
    ) {
      const text = String(value).trim();
      if (text) return text;
    }
  }
  return "";
};

const formatSqlEntityLabel = (item: any, ...nameCandidates: any[]): string => {
  if (item === null || item === undefined) return "";
  if (typeof item !== "object") return String(item);

  const rawId =
    item.id ??
    item.dbId ??
    item.db_id ??
    item.value?.id ??
    item.value?.dbId;

  const id =
    rawId !== null && rawId !== undefined && String(rawId).trim() !== ""
      ? Number(rawId)
      : null;

  const name = firstText(...nameCandidates);

  if (id !== null && Number.isFinite(id) && id > 0) {
    return name ? `(${id}) ${name}` : `(${id})`;
  }

  return name;
};

const formatBusinessEntityLabel = (item: any, businessId: any, ...nameCandidates: any[]): string => {
  if (item === null || item === undefined) return "";
  if (typeof item !== "object") return String(item);

  const displayId = firstText(businessId);
  const name = firstText(...nameCandidates);

  if (displayId) return name ? `(${displayId}) ${name}` : `(${displayId})`;
  return name;
};

const formatBundlePolicyLabel = (policy: any): string => {
  if (policy == null) return "";
  if (typeof policy !== "object") return String(policy);

  const policyName = firstText(policy.Policy_name, policy.policy_name, policy.name);
  const policyId = firstText(policy.Policy_id, policy.policy_id);
  const plans = Array.isArray(policy.plans) ? policy.plans : [];
  const planNames = plans
    .map((plan: any) =>
      typeof plan === "string"
        ? plan
        : firstText(plan?.Plan_name, plan?.plan_name, plan?.name)
    )
    .filter(Boolean);

  const policyLabel = formatBusinessEntityLabel(policy, policyId, policyName);
  return `${policyLabel}${planNames.length ? ` (${planNames.join(", ")})` : ""}`;
};

const formatBundlePatientLabel = (patient: any): string => {
  if (patient === null || patient === undefined) return "";
  if (typeof patient !== "object") return String(patient);

  const name = firstText(
    patient.full_name,
    patient.Full_name,
    patient.name,
    patient.Patient_name,
    patient.patient_name,
    [patient.First_name ?? patient.first_name, patient.Last_name ?? patient.last_name]
      .filter(Boolean)
      .join(" ")
  );

  const nationalId = firstText(patient.National_id, patient.national_id);
  const memberId = firstText(patient.Member_id, patient.member_id);
  const passport = firstText(
    patient.Patient_passport,
    patient.patient_passport,
    patient.passport
  );

  const identifiers = [
    nationalId ? `National ID: ${nationalId}` : "",
    memberId ? `Member ID: ${memberId}` : "",
    passport ? `Passport: ${passport}` : "",
  ].filter(Boolean);

  return identifiers.length > 0
    ? `${name || "Patient"} (${identifiers.join(" | ")})`
    : name;
};

let activityBundleDoctorMapPromise:
  | Promise<Map<string, { id: number; name: string }>>
  | null = null;

const getActivityBundleDoctorMap = async (): Promise<
  Map<string, { id: number; name: string }>
> => {
  if (activityBundleDoctorMapPromise) return activityBundleDoctorMapPromise;

  activityBundleDoctorMapPromise = DoctorAxiosInstance.get("getDoctor")
    .then((response) => {
      const raw =
        response.data?.result ??
        response.data?.data ??
        [];

      const rows = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.data)
          ? raw.data
          : [];

      const map = new Map<string, { id: number; name: string }>();

      rows.forEach((doctor: any) => {
        const sqlId = Number(
          doctor?.id ??
          doctor?.doctor_db_id ??
          doctor?.doctorDbId
        );

        const businessId = firstText(
          doctor?.Doctor_id,
          doctor?.doctor_id,
          doctor?.doctorId
        );

        const name = firstText(
          doctor?.Doctor_name,
          doctor?.doctor_name,
          doctor?.doctorName,
          doctor?.name
        );

        if (Number.isFinite(sqlId) && sqlId > 0 && businessId) {
          map.set(businessId, { id: sqlId, name });
        }
      });

      return map;
    })
    .catch(() => new Map<string, { id: number; name: string }>());

  return activityBundleDoctorMapPromise;
};

const enrichBundleDoctorSqlIds = async (item: any): Promise<any> => {
  if (!item || !Array.isArray(item.doctor) || item.doctor.length === 0) {
    return item;
  }

  const needsLookup = item.doctor.some(
    (doctor: any) =>
      doctor &&
      typeof doctor === "object" &&
      (doctor.id === null || doctor.id === undefined) &&
      firstText(doctor.Doctor_id, doctor.doctor_id, doctor.doctorId)
  );

  if (!needsLookup) return item;

  const doctorMap = await getActivityBundleDoctorMap();

  return {
    ...item,
    doctor: item.doctor.map((doctor: any) => {
      if (!doctor || typeof doctor !== "object" || doctor.id != null) {
        return doctor;
      }

      const businessId = firstText(
        doctor.Doctor_id,
        doctor.doctor_id,
        doctor.doctorId
      );

      const match = doctorMap.get(businessId);
      if (!match) return doctor;

      return {
        ...doctor,
        id: match.id,
        Doctor_name:
          doctor.Doctor_name ??
          doctor.doctor_name ??
          match.name,
      };
    }),
  };
};

const modeFromString = (val: string | boolean | undefined | null): "EXCLUSION" | "INCLUSION" | null => {
  if (val === "EXCLUDED" || val === true)  return "EXCLUSION";
  if (val === "INCLUDED")                  return "INCLUSION";
  return null;
};

const cardModeToString = (
  isActive: boolean,
  mode: "EXCLUSION" | "INCLUSION" | null,
  selectAll: boolean
): "INCLUDED" | "EXCLUDED" | "DISABLED" => {
  if (!isActive && !selectAll) return "DISABLED";
  if (mode === "EXCLUSION") return "EXCLUDED";
  if (mode === "INCLUSION") return "INCLUDED";
  return "INCLUDED";
};

const preserveScrollCheckbox = (e: React.ChangeEvent<HTMLInputElement>, fn: () => void) => {
  const el = e.currentTarget as HTMLElement;
  const dialog = el.closest('[role="dialog"]');
  const scrollable = dialog?.querySelector('.overflow-y-auto') as HTMLElement | null;
  const scrollTop = scrollable?.scrollTop ?? 0;
  fn();
  requestAnimationFrame(() => {
    if (scrollable) scrollable.scrollTop = scrollTop;
  });
};

// Referenced entity status is independent of the bundle relation status.
const referenceInactive = (entity: any): boolean => {
  if (!entity || typeof entity !== "object") return false;
  const value = entity.__inactive ?? entity.is_active ?? entity.Is_active ?? entity.isActive ?? entity.status ?? entity.Status;
  return value === true && entity.__inactive === true || value === false || value === 0 ||
    (typeof value === "string" && ["inactive", "disabled", "false", "0"].includes(value.trim().toLowerCase()));
};
const markReference = (entity: any, selected: any) => ({ ...selected, __inactive: referenceInactive(entity),
  is_active: entity?.is_active, status: entity?.status ?? entity?.Status });
const referenceKey = (entity: any): string => String(entity?.value?.id ?? entity?.value ?? entity?.id ?? entity?.Policy_id ?? "");
const preserveLocked = <T extends { value?: any; id?: any; __inactive?: boolean }>(previous: T[], next: T[] | null | undefined): T[] => {
  const incoming = Array.isArray(next) ? next : [];
  const normalized = incoming.map(candidate => {
    const original = previous.find(p => referenceKey(p) === referenceKey(candidate));
    return original?.__inactive ? { ...candidate, ...original } : candidate;
  });
  return [...normalized, ...previous.filter(p => p.__inactive && !normalized.some(n => referenceKey(p) === referenceKey(n)))];
};
const hasLocked = (values: any[] | undefined): boolean => Array.isArray(values) && values.some(referenceInactive);

/** Paint native inline react-select tags, never external tags or SVG className access. */
const ReferenceSelect: React.FC<{
  values: any[];
  onChange: (values: any[]) => void;
  children: React.ReactElement<any>;
}> = ({ values, onChange, children }) => {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const [revision, bumpRevision] = React.useState(0);
  const valuesRef = React.useRef(values);
  valuesRef.current = values;
  const onSafeChange = (incoming: any) => {
    const prior = valuesRef.current;
    const next = preserveLocked(prior, Array.isArray(incoming) ? incoming : []);
    if (next.length > (Array.isArray(incoming) ? incoming.length : 0)) {
      // Shared selectors may own internal state; restore protected options on attempted deletion.
      bumpRevision(v => v + 1);
    }
    if (typeof children.props.onChange === "function") children.props.onChange(next);
    else onChange(next);
  };
  React.useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const paint = () => {
      const chips = Array.from(root.querySelectorAll<HTMLElement>('[class*="multiValue"]')).filter(el => {
        const classValue = el.getAttribute("class") || "";
        return !classValue.includes("multiValueLabel") && !classValue.includes("multiValueRemove") &&
          !!el.querySelector('[class*="multiValueLabel"]');
      });
      chips.forEach((chip, index) => {
        if (referenceInactive(valuesRef.current[index])) chip.setAttribute('data-locked-reference', 'true');
        else chip.removeAttribute('data-locked-reference');
      });
    };
    paint();
    const observer = new MutationObserver(paint);
    observer.observe(root, { subtree: true, childList: true });
    return () => observer.disconnect();
  }, [values, revision]);
  return <div ref={rootRef} className="bundle-locked-reference">
    <style>{`.bundle-locked-reference [data-locked-reference="true"] {
      background: #d1d5db !important; border: 1px solid #94a3b8 !important; opacity: 1 !important;
    }
    .bundle-locked-reference [data-locked-reference="true"] [class*="multiValueLabel"] {
      color: #334155 !important; font-weight: 600 !important;
    }
    .bundle-locked-reference [data-locked-reference="true"] [class*="multiValueRemove"] { display: none !important; }`}</style>
    {React.cloneElement(children, { key: revision, defaultValue: values, onChange: onSafeChange })}
  </div>;
};
const statusTag = (entity: any, label: string) => referenceInactive(entity) ? `${label} · Inactive` : label;

// ─── SectionCard ──────────────────────────────────────────────────────────────
const SectionCard: React.FC<{ num: number; title: string; subtitle?: string; children: React.ReactNode }> = ({ num, title, subtitle, children }) => (
  <div className="relative z-10 border border-gray-200 rounded-xl overflow-visible mb-4 bg-white shadow-sm">
    <div className="flex items-center gap-3 rounded-t-xl px-5 py-3 bg-gray-50 border-b border-gray-200">
      <span className="w-6 h-6 rounded-full bg-blue-700 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">{num}</span>
      <div>
        <p className="text-sm font-semibold text-gray-800">{title}</p>
        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
      </div>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

// ─── ActivitySourcePicker (wide segmented control + content) ──────────────────
const ActivitySourcePicker: React.FC<{
  title: string;
  selected: string | null;
  onSelect: (key: string | null) => void;
  codeOptionKey: string;
  counts: { code: number; medical: number; custom: number };
  children: React.ReactNode;
}> = ({ title, selected, onSelect, codeOptionKey, counts, children }) => {
  const options = [
    { key: codeOptionKey, label: "Activity Codes", icon: "fa-code" },
    { key: "medical_Activity_groups", label: "Medical Groups", icon: "fa-layer-group" },
    { key: "custom_Activity_groups", label: "Custom Groups", icon: "fa-cubes" },
  ];
  return (
    <div>
      <Label className="mb-2 block">{title}</Label>
      <div className="flex bg-slate-100 rounded-lg p-1 border border-slate-200 gap-1">
        {options.map((opt) => {
          const isSelected = selected === opt.key;
          const count = opt.key === codeOptionKey ? counts.code : opt.key === "medical_Activity_groups" ? counts.medical : counts.custom;
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => onSelect(isSelected ? null : opt.key)}
              className={`flex-1 flex items-center justify-center gap-2 rounded-md px-3 py-2.5 text-sm font-medium transition-all ${isSelected ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            >
              <i className={`fa-solid ${opt.icon} text-xs`}></i>
              <span>{opt.label}</span>
              {count > 0 && (
                <span className={`text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center ${isSelected ? "bg-blue-100 text-blue-700" : "bg-slate-300 text-slate-600"}`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {selected && (
        <div className="relative z-50 mt-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
          {children}
        </div>
      )}
    </div>
  );
};

// ─── EntityCard Colors ────────────────────────────────────────────────────────
const entityColors: Record<string, { border: string; bg: string; text: string; badgeBg: string }> = {
  blue:    { border: "border-blue-200",    bg: "bg-blue-50",    text: "text-blue-700",    badgeBg: "bg-blue-50 text-blue-700 border-blue-200"    },
  cyan:    { border: "border-cyan-200",    bg: "bg-cyan-50",    text: "text-cyan-700",    badgeBg: "bg-cyan-50 text-cyan-700 border-cyan-200"    },
  purple:  { border: "border-purple-200",  bg: "bg-purple-50",  text: "text-purple-700",  badgeBg: "bg-purple-50 text-purple-700 border-purple-200"  },
  amber:   { border: "border-amber-200",   bg: "bg-amber-50",   text: "text-amber-700",   badgeBg: "bg-amber-50 text-amber-700 border-amber-200"   },
  emerald: { border: "border-emerald-200", bg: "bg-emerald-50", text: "text-emerald-700", badgeBg: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  pink:    { border: "border-pink-200",    bg: "bg-pink-50",    text: "text-pink-700",    badgeBg: "bg-pink-50 text-pink-700 border-pink-200"    },
  yellow:  { border: "border-yellow-200",  bg: "bg-yellow-50",  text: "text-yellow-700",  badgeBg: "bg-yellow-50 text-yellow-700 border-yellow-200"  },
  indigo:  { border: "border-indigo-200",  bg: "bg-indigo-50",  text: "text-indigo-700",  badgeBg: "bg-indigo-50 text-indigo-700 border-indigo-200"  },
  slate:   { border: "border-slate-200",   bg: "bg-slate-50",   text: "text-slate-700",   badgeBg: "bg-slate-50 text-slate-700 border-slate-200"   },
  teal:    { border: "border-teal-200",    bg: "bg-teal-50",    text: "text-teal-700",    badgeBg: "bg-teal-50 text-teal-700 border-teal-200"    },
};

// ─── EntityCard Component ─────────────────────────────────────────────────────
interface EntityCardProps {
  label: string;
  icon: string;
  color: string;
  isActive: boolean;
  mode: "EXCLUSION" | "INCLUSION" | null;
  onExclusion: () => void;
  onInclusion: () => void;
  onClear: () => void;
  lockedReferences?: boolean;
  children: React.ReactNode;
}

const EntityCard: React.FC<EntityCardProps> = ({
  label, icon, color, isActive, mode, onExclusion, onInclusion, onClear, lockedReferences = false, children,
}) => {
  const c = entityColors[color] ?? entityColors.slate;

  const preserveScroll = (fn: () => void) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const dialog = (e.currentTarget as HTMLElement).closest('[role="dialog"]');
    const scrollable = dialog?.querySelector('.overflow-y-auto') as HTMLElement | null;
    const scrollTop = scrollable?.scrollTop ?? 0;
    if (lockedReferences) return;
    fn();
    requestAnimationFrame(() => {
      if (scrollable) scrollable.scrollTop = scrollTop;
    });
  };

  return (
    <div className={`bg-white rounded-xl border p-4 transition-all relative ${isActive ? `${c.border} shadow-sm` : "border-slate-100"}`}>
      {isActive && (
        <button
          type="button"
          onClick={preserveScroll(onClear)}
          className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-slate-50 text-slate-400 hover:bg-red-50 hover:text-red-500 transition-all border border-slate-100 shadow-sm z-10"
          title="Clear"
        >
          <i className="fa-solid fa-xmark text-[14px]"></i>
        </button>
      )}
      <div className="flex flex-col items-center gap-4">
        <span className={`px-4 py-1 rounded-full text-[10px] font-black tracking-tighter border flex items-center gap-2 ${isActive ? c.badgeBg : "bg-slate-50 text-slate-400 border-slate-200"}`}>
          <i className={`fa-solid ${icon} text-[12px]`}></i> {label}
        </span>
        <div className="flex bg-slate-100/80 rounded-lg p-1 border border-slate-200 w-full shadow-inner">
          <button
            type="button"
            onClick={preserveScroll(onExclusion)}
            className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${isActive && mode === "EXCLUSION" ? "bg-red-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}
          >
            Exclude
          </button>
          <button
            type="button"
            onClick={preserveScroll(onInclusion)}
            className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${isActive && mode === "INCLUSION" ? "bg-emerald-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}
          >
            Include
          </button>
        </div>
      </div>
      {isActive && (
        <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
          {children}
        </div>
      )}
    </div>
  );
};

// ─── Activity Badge Cell ──────────────────────────────────────────────────────
const ActivityCell: React.FC<{
  items: CodeWithType[];
  groups: string[];
  searchTerm?: string;
  onShowAll: (items: CodeWithType[], groups: string[]) => void;
}> = ({ items, groups, searchTerm = "", onShowAll }) => {
  if (items.length === 0 && groups.length === 0) {
    return <div className="w-full text-center text-gray-400">-</div>;
  }

  const allDisplay = [
    ...items.map((item) => ({
      label: item.code.toString(),
      type: item.type,
      isGroup: false,
      originalItem: item,
    })),
    ...groups.map((group) => ({
      label: group,
      type: group.startsWith("[Medical]") ? "Medical" : "Custom",
      isGroup: true,
      originalItem: null,
    })),
  ];

  const normalizedSearch = searchTerm.toLowerCase().trim();
  const hasSearch = normalizedSearch.length >= 2;
  const matchingDisplay = hasSearch
    ? allDisplay.filter((entry) => entry.label.toLowerCase().includes(normalizedSearch))
    : [];
  const hasMatches = hasSearch && matchingDisplay.length > 0;
  const displaySource = hasMatches ? matchingDisplay : allDisplay;
  const displayedValues = displaySource.slice(0, 3);
  const remainingCount = Math.max(0, displaySource.length - displayedValues.length);

  const dialogItems = hasMatches
    ? matchingDisplay
        .filter((entry) => !entry.isGroup && entry.originalItem)
        .map((entry) => entry.originalItem as CodeWithType)
    : items;

  const dialogGroups = hasMatches
    ? matchingDisplay.filter((entry) => entry.isGroup).map((entry) => entry.label)
    : groups;

  return (
    <div className="w-full px-3 py-2">
      <div className="flex flex-wrap justify-center gap-2 mb-2">
        {displayedValues.map((entry, index) => {
          const isMatching = hasSearch && entry.label.toLowerCase().includes(normalizedSearch);
          const defaultColor = entry.isGroup
            ? entry.type === "Medical"
              ? { bg: "bg-slate-50", border: "border-slate-400", text: "text-slate-700" }
              : { bg: "bg-blue-50", border: "border-blue-300", text: "text-blue-800" }
            : getActivityTypeColor(entry.type);
          const color = isMatching
            ? { bg: "bg-yellow-200", border: "border-yellow-400", text: "text-yellow-900" }
            : defaultColor;

          return (
            <div
              key={`${entry.type}-${entry.label}-${index}`}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all ${color.bg} ${color.border}`}
            >
              <span className={`font-semibold text-sm ${color.text}`}>{entry.label}</span>
              <span className={`text-[10px] font-bold uppercase ${color.text}`}>{entry.type}</span>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-gray-200">
        <span className="text-xs font-bold text-gray-600">
          {hasMatches ? `${matchingDisplay.length}/${allDisplay.length}` : allDisplay.length}
        </span>

        {remainingCount > 0 && (
          <button
            onClick={(event) => {
              event.stopPropagation();
              onShowAll(dialogItems, dialogGroups);
            }}
            className={`text-xs font-semibold flex items-center gap-1 ${
              hasMatches
                ? "text-yellow-600 hover:text-yellow-800"
                : "text-blue-600 hover:text-blue-800"
            }`}
          >
            <span>+{remainingCount} more</span>
            <i className="fa-solid fa-plus text-[10px]"></i>
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Normalize raw Activity Bundle API item ──────────────────────────────────
const normalizeItem = (item: any): Datum => {
  const typeMap: Record<string, string> = {
    cpt: "CPT",
    hcpcs: "HCPCS",
    dental: "Dental",
    drugs: "Drugs",
  };

  const mainCodes: string[] = Array.isArray(item?.main_activity)
    ? item.main_activity.map((code: any) => String(code))
    : [];
  const rawMainTypes: string[] = Array.isArray(item?.main_activity_type)
    ? item.main_activity_type
    : Array.isArray(item?.activity_type)
      ? item.activity_type
      : [];

  const activityCodes: string[] = Array.isArray(item?.activities)
    ? item.activities.map((code: any) => String(code))
    : Array.isArray(item?.not_allowed_activity)
      ? item.not_allowed_activity.map((code: any) => String(code))
      : [];
  const rawActivityTypes: string[] = Array.isArray(item?.activities_type)
    ? item.activities_type
    : Array.isArray(item?.activity_types)
      ? item.activity_types
      : [];

  const main_activity_types: CodeWithType[] = mainCodes.map((code, index) => {
    const rawType = String(rawMainTypes[index] ?? rawMainTypes[0] ?? "cpt");
    return {
      code,
      type: typeMap[rawType.toLowerCase()] || rawType || "CPT",
    };
  });

  const not_allowed_activity_types: CodeWithType[] = activityCodes.map(
    (code, index) => {
      const rawType = String(
        rawActivityTypes[index] ?? rawActivityTypes[0] ?? "cpt"
      );
      return {
        code,
        type: typeMap[rawType.toLowerCase()] || rawType || "CPT",
      };
    }
  );

  const modeStr = (val: any): boolean => val === "EXCLUDED";

  return {
    ...item,

    // Canonical Activity Bundle backend -> existing UI names.
    main_activity: mainCodes,
    activity_type: rawMainTypes,
    main_activity_types,
    main_activity_medicalgroups:
      item?.medical_groups_main ?? item?.main_activity_medicalgroups ?? [],
    main_activity_customgroups:
      item?.custom_groups_main ?? item?.main_activity_customgroups ?? [],

    not_allowed_activity: activityCodes,
    activity_types: rawActivityTypes,
    not_allowed_activity_types,
    not_allowed_activity_medicalgroups:
      item?.medical_groups_activities ??
      item?.not_allowed_activity_medicalgroups ??
      [],
    not_allowed_activity_customgroups:
      item?.custom_groups_activities ??
      item?.not_allowed_activity_customgroups ??
      [],

    items_order: getOrderedItems(item),
    networks: item?.network ?? item?.networks ?? [],
    patients: item?.patient ?? item?.patients ?? [],
    insuranceplan: item?.insuranceplan ?? [],

    apply_to_other_providers: modeStr(item?.provider_mode),
    apply_to_other_doctor: modeStr(item?.doctor_mode),
    apply_to_other_networks: modeStr(item?.network_mode),
    apply_to_other_insuranceplan: modeStr(item?.insuranceplan_mode),
    apply_to_other_payer: modeStr(item?.payer_mode),
    apply_to_other_patients: modeStr(item?.patient_mode),
    apply_to_other_policy: modeStr(item?.policy_mode),
    apply_to_other_user: modeStr(item?.user_mode),
    apply_to_vip: modeStr(item?.vip_mode),

    select_all_providers: item?.provider_all ?? false,
    select_all_doctor: item?.doctor_all ?? false,
    select_all_networks: item?.network_all ?? false,
    select_all_insuranceplan: item?.insuranceplan_all ?? false,
    select_all_payer: item?.payer_all ?? false,
    select_all_patients: item?.patient_all ?? false,
    select_all_policy: item?.policy_all ?? false,
    select_all_user: item?.user_all ?? false,
    select_all_vip: item?.vip_all ?? false,
    specialty: item?.specialty ?? [],
    specialty_mode: item?.specialty_mode ?? "DISABLED",
    select_all_specialty: item?.specialty_all ?? false,
  };
};
// ═══════════════════════════════════════════════════════════════════════════════
const ActivityNotAllowedIncludeActivity: React.FC = () => {
  const [items, setItems] = useState<activityNotAllowedIncludeActivity>({ data: [] });

  const [relationName, setRelationName] = useState<string>("");
  const [isActive, setIsActive] = useState<boolean>(true);
  const [editIndex, setEditIndex] = useState<null | number>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [selectedMainOption, setSelectedMainOption] = useState<string | null>(null);
  const [mainActivitiesByType, setMainActivitiesByType] = useState<ActivitiesByType>({ ...EMPTY_BY_TYPE });
  const [mainActivityMedicalGroups, setMainActivityMedicalGroups] = useState<Option[]>([]);
  const [mainActivityCustomGroups, setMainActivityCustomGroups] = useState<Option[]>([]);

  const [activityGroups, setActivityGroups] = useState<ActivitySelectionGroup[]>([
    createActivitySelectionGroup(),
  ]);

  const [showEntities, setShowEntities] = useState(false);

  // ── Provider ──
  const [provider, setProvider]                     = useState<Option[]>([]);
  const [providerMode, setProviderMode]             = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isProviderActive, setIsProviderActive]     = useState(false);
  const [selectAllProviders, setSelectAllProviders] = useState(false);
  const [clearProvider, setClearProvider]           = useState(false);

  // ── Doctor ──
  const [doctor, setDoctor]                   = useState<Option[]>([]);
  const [doctorMode, setDoctorMode]           = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isDoctorActive, setIsDoctorActive]   = useState(false);
  const [selectAllDoctor, setSelectAllDoctor] = useState(false);
  const [clearDoctor, setClearDoctor]         = useState(false);

  // ── Networks ──
  const [networks, setNetworks]               = useState<Option[]>([]);
  const [networkMode, setNetworkMode]         = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isNetworkActive, setIsNetworkActive] = useState(false);
  const [selectAllNetworks, setSelectAllNetworks] = useState(false);
  const [clearNetworks, setClearNetworks]     = useState(false);

  // ── Insurance ──
  const [insuranceplan, setInsuranceplan]     = useState<Option[]>([]);
  const [insuranceMode, setInsuranceMode]     = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isInsuranceActive, setIsInsuranceActive] = useState(false);
  const [selectAllInsurance, setSelectAllInsurance] = useState(false);
  const [clearInsuranceplan, setClearInsuranceplan] = useState(false);

  // ── Payer ──
  const [Payer, setPayer]                   = useState<string[]>([]);
  const [payerMode, setPayerMode]           = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isPayerActive, setIsPayerActive]   = useState(false);
  const [selectAllPayer, setSelectAllPayer] = useState(false);

  // ── Policy ──
  const [policy, setPolicy]                   = useState<PolicyItem[]>([]);
  const [policyMode, setPolicyMode]           = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isPolicyActive, setIsPolicyActive]   = useState(false);
  const [selectAllPolicy, setSelectAllPolicy] = useState(false);
  const [clearPolicy, setClearPolicy]         = useState(false);

  // ── Patients ──
  const [patients, setPatients]               = useState<Option[]>([]);
  const [patientMode, setPatientMode]         = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isPatientActive, setIsPatientActive] = useState(false);
  const [selectAllPatients, setSelectAllPatients] = useState(false);
  const [clearPatient, setClearPatient]       = useState(false);

  // ── VIP ──
  const [vip, setVip]                   = useState<Option[]>([]);
  const [vipMode, setVipMode]           = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isVipActive, setIsVipActive]   = useState(false);
  const [selectAllVip, setSelectAllVip] = useState(false);
  const [clearVip, setClearVip]         = useState(false);

  // ── User Level ──
  const [userLevel, setUserLevel]           = useState<string[]>([]);
  const [userMode, setUserMode]             = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isUserActive, setIsUserActive]     = useState(false);
  const [selectAllUser, setSelectAllUser]   = useState(false);

  // ── Specialty ──
  const [specialty, setSpecialty]                     = useState<Option[]>([]);
  const [specialtyMode, setSpecialtyMode]             = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isSpecialtyActive, setIsSpecialtyActive]     = useState(false);
  const [selectAllSpecialty, setSelectAllSpecialty]   = useState(false);
  const [clearSpecialty, setClearSpecialty]           = useState(false);

  // clear flags
  const [clearMainActivityMedicalGroups, setClearMainActivityMedicalGroups]           = useState(false);
  const [clearMainActivityCustomGroups, setClearMainActivityCustomGroups]             = useState(false);

  const [selectedItem, setSelectedItem] = useState<Datum | null>(null);
  const dialogRef = useRef<HTMLDialogElement | null>(null);

  const [allCodesDialogOpen, setAllCodesDialogOpen]   = useState(false);
  const [allCodesDialogItems, setAllCodesDialogItems] = useState<CodeWithType[]>([]);
  const [allCodesDialogGroups, setAllCodesDialogGroups] = useState<string[]>([]);
  const [allCodesDialogTitle, setAllCodesDialogTitle] = useState("");

  const [filterStatus, setFilterStatus]                   = useState<string>("");

  const [deletedFilter, setDeletedFilter] = useState<ServerDeletedFilterValue>("false");
  const [filterSearch, setFilterSearch] = useState<string>("");

  const [usersMap, setUsersMap] = useState<Record<number, string>>({});
  const [showAllUpdates, setShowAllUpdates] = useState(false);

  // ── Force / Hard Delete Dialog state ──────────────────────────────────────
  const [forceDialogOpen, setForceDialogOpen] = useState(false);
  const [forceId, setForceId] = useState<string | number | null>(null);
  const [forceError, setForceError] = useState<string>("");
  const [forceAction, setForceAction] = useState<"hardDelete" | null>(null);

  const token = localStorage.getItem("token") || "";
  const { toast } = useToast();

  // ── Role-based visibility ──────────────────────────────────────────────────
  const userRole = getUserRole();
  const canManageDeleted = userRole === "system_admin" || userRole === "super_super_admin";

  useEffect(() => { fetchUsers(); }, []);

  // ── resetAllEntityStates ──────────────────────────────────────────────────
  const resetAllEntityStates = () => {
    setProvider([]);      setProviderMode(null);  setIsProviderActive(false);  setSelectAllProviders(false);
    setDoctor([]);        setDoctorMode(null);    setIsDoctorActive(false);    setSelectAllDoctor(false);
    setNetworks([]);      setNetworkMode(null);   setIsNetworkActive(false);   setSelectAllNetworks(false);
    setInsuranceplan([]); setInsuranceMode(null); setIsInsuranceActive(false); setSelectAllInsurance(false);
    setPayer([]);         setPayerMode(null);     setIsPayerActive(false);     setSelectAllPayer(false);
    setPolicy([]);        setPolicyMode(null);    setIsPolicyActive(false);    setSelectAllPolicy(false);
    setPatients([]);      setPatientMode(null);   setIsPatientActive(false);   setSelectAllPatients(false);
    setVip([]);           setVipMode(null);       setIsVipActive(false);       setSelectAllVip(false);
    setUserLevel([]);     setUserMode(null);      setIsUserActive(false);      setSelectAllUser(false);
    setSpecialty([]);     setSpecialtyMode(null); setIsSpecialtyActive(false); setSelectAllSpecialty(false);
  };

  const handleToggleEntities = () => {
    if (showEntities && [provider, doctor, networks, insuranceplan, policy, patients, vip, specialty].some(hasLocked)) return;
    if (showEntities) resetAllEntityStates();
    setShowEntities((prev) => !prev);
  };

  const validateEntities = (): string | null => {
    if (!showEntities) return null;
    const checks = [
      { isActive: isProviderActive,   hasData: selectAllProviders   || provider.length > 0,     label: "Providers"      },
      { isActive: isDoctorActive,     hasData: selectAllDoctor      || doctor.length > 0,        label: "Doctor"         },
      { isActive: isNetworkActive,    hasData: selectAllNetworks    || networks.length > 0,      label: "Networks"       },
      { isActive: isInsuranceActive,  hasData: selectAllInsurance   || insuranceplan.length > 0, label: "Insurance Plan" },
      { isActive: isPolicyActive,     hasData: selectAllPolicy      || policy.length > 0,        label: "Policy"         },
      { isActive: isPatientActive,    hasData: selectAllPatients    || patients.length > 0,      label: "Patients"       },
      { isActive: isVipActive,        hasData: selectAllVip         || vip.length > 0,           label: "VIP"            },
      { isActive: isPayerActive,      hasData: selectAllPayer       || Payer.length > 0,         label: "Payer"          },
      { isActive: isUserActive,       hasData: selectAllUser        || userLevel.length > 0,     label: "User Level"     },
      { isActive: isSpecialtyActive,  hasData: selectAllSpecialty   || specialty.length > 0,     label: "Specialty"      },
    ];
    const empty = checks.filter(c => c.isActive && !c.hasData).map(c => c.label);
    if (empty.length > 0) {
      return `The following cards are open but have no data selected: ${empty.join(", ")}. Please select at least one item for each, or close them using the × button.`;
    }
    const anyActive = checks.some(c => c.isActive);
    if (!anyActive) {
      return "Entities Configuration is open but no entity card is selected. Please activate at least one entity or turn off the toggle.";
    }
    return null;
  };

  const clear = () => {
    setRelationName("");
    setSelectedMainOption(null);
    setMainActivitiesByType({ ...EMPTY_BY_TYPE });
    setMainActivityMedicalGroups([]);
    setMainActivityCustomGroups([]);
    setActivityGroups([createActivitySelectionGroup()]);
    setIsActive(true);
    setShowEntities(false);
    resetAllEntityStates();
    setClearMainActivityMedicalGroups(true);
    setClearMainActivityCustomGroups(true);
    setClearProvider(true);
    setClearDoctor(true);
    setClearNetworks(true);
    setClearInsuranceplan(true);
    setClearPatient(true);
    setClearPolicy(true);
    setClearVip(true);
    setClearSpecialty(true);
  };

  const addActivityGroup = () => {
    setActivityGroups((previous) => [
      ...previous.map((group) => ({ ...group, isExpanded: false })),
      createActivitySelectionGroup({ isExpanded: true }),
    ]);
  };

  const removeActivityGroup = (groupId: string) => {
    setActivityGroups((previous) => {
      const target = previous.find(group => group.id === groupId);
      if (target && (hasLocked(target.medicalGroups) || hasLocked(target.customGroups))) return previous;
      const remaining = previous.filter((group) => group.id !== groupId);
      return remaining.length > 0
        ? remaining
        : [createActivitySelectionGroup({ isExpanded: true })];
    });
  };

  const updateActivityGroup = (
    groupId: string,
    updater: (group: ActivitySelectionGroup) => ActivitySelectionGroup,
  ) => {
    setActivityGroups((previous) =>
      previous.map((group) =>
        group.id === groupId ? updater(group) : group,
      ),
    );
  };

  const toggleActivityGroup = (groupId: string) => {
    setActivityGroups((previous) =>
      previous.map((group) =>
        group.id === groupId
          ? { ...group, isExpanded: !group.isExpanded }
          : group,
      ),
    );
  };

  const findDuplicateActivity = (groups: ActivitySelectionGroup[] = activityGroups): { key: string; first: number; second: number } | null => {
    const seen = new Map<string, number>();
    for (let groupIndex = 0; groupIndex < groups.length; groupIndex += 1) {
      const ordered = buildItemsOrder([groups[groupIndex]]).map((item) => ({ ...item, activity_order: groupIndex + 1 }));
      for (const item of ordered) {
        const key = orderedItemKey(item);
        const first = seen.get(key);
        if (first != null && first !== groupIndex + 1) return { key, first, second: groupIndex + 1 };
        seen.set(key, groupIndex + 1);
      }
    }
    return null;
  };

  const filterDuplicateSelections = (
    groupId: string,
    kind: "code" | "medicalgroup" | "customgroup",
    selected: Option[],
    activityType?: keyof ActivitiesByType,
  ): Option[] => {
    const currentIndex = activityGroups.findIndex((group) => group.id === groupId);
    const used = new Map<string, number>();

    activityGroups.forEach((group, index) => {
      if (group.id === groupId) return;
      buildItemsOrder([group]).forEach((item) => {
        const normalized = { ...item, activity_order: index + 1 };
        used.set(orderedItemKey(normalized), index + 1);
      });
    });

    const accepted: Option[] = [];
    const rejected: string[] = [];
    selected.forEach((option) => {
      const candidate: OrderedActivityItem = kind === "code"
        ? { type: "code", code: option.value as string | number, activity_type: String(activityType || "CPT").toLowerCase(), side: "not_allowed", activity_order: currentIndex + 1 }
        : { type: kind, id: option.value as string | number, name: String(option.label ?? ""), side: "not_allowed", activity_order: currentIndex + 1 };
      const usedIn = used.get(orderedItemKey(candidate));
      if (usedIn) rejected.push(`${option.label ?? option.value} (Activity ${usedIn})`);
      else accepted.push(option);
    });

    if (rejected.length > 0) {
      toast({
        title: "Duplicate activity is not allowed",
        description: `${rejected.join(", ")} already selected and cannot be added to Activity ${currentIndex + 1}.`,
        variant: "destructive",
      });
    }
    return accepted;
  };

  const buildProviderValues = (arr: Option[]) =>
    arr.map((o: any) => {
      if (o.providerId && o.providerName) return { Provider_id: o.providerId, Provider_name: o.providerName };
      const match = o.label?.match(/^\((.+)\) (.+)$/);
      if (match) return { Provider_id: match[1].trim(), Provider_name: match[2].trim() };
      return { Provider_id: o.value?.toString() || "", Provider_name: o.label?.toString() || "" };
    });

  const buildPolicyValues = (arr: PolicyItem[]) =>
    arr.map((p: any) => {
      if (typeof p === "object" && p.Policy_id && !p.value)
        return { id: p.id || 0, Policy_id: p.Policy_id, Policy_name: p.Policy_name, plans: p.plans || [] };
      if (typeof p === "object" && p.value)
        return { id: p.value.id || 0, Policy_id: p.value.Policy_id || "", Policy_name: p.value.Policy_name || "", plans: p.value.plans || [] };
      if (typeof p === "string") return { id: 0, Policy_id: p, Policy_name: p, plans: [] };
      return { id: 0, Policy_id: "", Policy_name: "", plans: [] };
    });

  const getDbId = (value: any): number | null => {
    if (value === null || value === undefined) return null;

    if (typeof value === "object") {
      const candidate =
        value.dbId ??
        value.id ??
        value.value?.id ??
        value.value?.dbId ??
        value.value ??
        value.patient_id ??
        value.network_id ??
        value.plan_id ??
        value.specialty_id ??
        value.vip_id;

      if (
        candidate !== undefined &&
        candidate !== null &&
        typeof candidate !== "object"
      ) {
        const id = Number(candidate);
        return Number.isInteger(id) && id > 0 ? id : null;
      }

      return null;
    }

    const id = Number(value);
    return Number.isInteger(id) && id > 0 ? id : null;
  };

  const toNumericIds = (values: any[] = []): number[] =>
    values
      .map(getDbId)
      .filter((id): id is number => id !== null);

  const toStringDbIds = (values: any[] = []): string[] =>
    toNumericIds(values).map(String);

  const getPolicyIds = (values: any[] = []): number[] =>
    values
      .map((policyItem: any) =>
        getDbId(
          policyItem?.id ??
            policyItem?.value?.id ??
            policyItem?.dbId ??
            (typeof policyItem?.value !== "object"
              ? policyItem?.value
              : null)
        )
      )
      .filter((id): id is number => id !== null);

  const buildPayload = () => {
    const mainFlat = flattenByType(mainActivitiesByType);
    const activitiesFlat = activityGroups.flatMap((group) =>
      flattenByType(group.activitiesByType)
    );
    const activityMedicalGroups = activityGroups.flatMap(
      (group) => group.medicalGroups
    );
    const activityCustomGroups = activityGroups.flatMap(
      (group) => group.customGroups
    );

    return {
      relation_name: relationName.trim(),

      // Canonical Activity Bundle fields from the backend.
      main_activity: mainFlat.map((item) => String(item.code)),
      main_activity_type: mainFlat.map((item) =>
        String(item.type).toLowerCase()
      ),
      medical_groups_main: toNumericIds(mainActivityMedicalGroups),
      custom_groups_main: toNumericIds(mainActivityCustomGroups),

      activities: activitiesFlat.map((item) => String(item.code)),
      activities_type: activitiesFlat.map((item) =>
        String(item.type).toLowerCase()
      ),
      medical_groups_activities: toNumericIds(activityMedicalGroups),
      custom_groups_activities: toNumericIds(activityCustomGroups),

      items_order: buildItemsOrder(activityGroups),

      provider: selectAllProviders ? [] : toStringDbIds(provider),
      provider_mode: cardModeToString(
        isProviderActive,
        providerMode,
        selectAllProviders
      ),
      provider_all: selectAllProviders,

      network: selectAllNetworks ? [] : toNumericIds(networks),
      network_mode: cardModeToString(
        isNetworkActive,
        networkMode,
        selectAllNetworks
      ),
      network_all: selectAllNetworks,

      policy: selectAllPolicy ? [] : getPolicyIds(policy),
      policy_mode: cardModeToString(
        isPolicyActive,
        policyMode,
        selectAllPolicy
      ),
      policy_all: selectAllPolicy,

      insuranceplan: selectAllInsurance
        ? []
        : toNumericIds(insuranceplan),
      insuranceplan_mode: cardModeToString(
        isInsuranceActive,
        insuranceMode,
        selectAllInsurance
      ),
      insuranceplan_all: selectAllInsurance,

      payer: selectAllPayer ? [] : Payer,
      payer_mode: cardModeToString(
        isPayerActive,
        payerMode,
        selectAllPayer
      ),
      payer_all: selectAllPayer,

      patient: selectAllPatients ? [] : toNumericIds(patients),
      patient_mode: cardModeToString(
        isPatientActive,
        patientMode,
        selectAllPatients
      ),
      patient_all: selectAllPatients,

      user_level: selectAllUser ? [] : userLevel,
      user_mode: cardModeToString(isUserActive, userMode, selectAllUser),
      user_all: selectAllUser,

      vip: selectAllVip ? [] : toNumericIds(vip),
      vip_mode: cardModeToString(isVipActive, vipMode, selectAllVip),
      vip_all: selectAllVip,

      doctor: selectAllDoctor ? [] : toStringDbIds(doctor),
      doctor_mode: cardModeToString(
        isDoctorActive,
        doctorMode,
        selectAllDoctor
      ),
      doctor_all: selectAllDoctor,

      specialty: selectAllSpecialty ? [] : toNumericIds(specialty),
      specialty_mode: cardModeToString(
        isSpecialtyActive,
        specialtyMode,
        selectAllSpecialty
      ),
      specialty_all: selectAllSpecialty,

      is_active: isActive,
    };
  };
  // ── Server-side pagination, search, and filters ─────────────────────────────
  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalCount,
    allowedPageSizes,
    isTableLoading,
    manualPagination,
    fetchItems,
  } = useServerPaginatedList<
    Datum,
    {
      search: string;
      status: string;
      deleted: ServerDeletedFilterValue;
    }
  >({
    filters: {
      search: filterSearch,
      status: filterStatus,
      deleted: deletedFilter,
    },
    fetchPage: ({ page, limit, filters }) =>
      ActivityBundleAxiosInstance.get(
        "getAllActivityBundle",
        {
          params: {
            page,
            limit,
            search: filters.search.trim() || undefined,
            status:
              filters.status === "true"
                ? "active"
                : filters.status === "false"
                  ? "inactive"
                  : undefined,
            is_deleted: filters.deleted,
          },
        },
      ),
    setRows: (rows) => setItems({ data: rows }),
    normalizeRow: normalizeItem,
    // Sort IDs in ascending order.
    sortRows: (rows) =>
      [...rows].sort((a: any, b: any) => Number(a.id) - Number(b.id)),
    onError: (error) => {
      console.error("Failed to fetch Activity Bundle", error);
      toast({
        title: "Error",
        description: "Failed to fetch Activity Bundle",
        variant: "destructive",
      });
    },
  });

  const fetchUsers = async () => {
    try {
      const res = await LoginAxiosInstance.get("/");
      const raw: any[] = res.data?.users ?? res.data?.user ?? res.data ?? [];
      const map: Record<number, string> = {};
      raw.forEach((u: any) => {
        if (u.id != null) map[Number(u.id)] = u.name ?? u.username ?? String(u.id);
      });
      setUsersMap(map);
    } catch {}
  };

  const resolveUserName = (val: any): string => {
    if (val == null) return "—";
    if (typeof val === "object") return val.name ?? val.username ?? "—";
    if (typeof val === "string" && isNaN(Number(val))) return val;
    return usersMap[Number(val)] ?? String(val);
  };

  const filteredData = items.data;

  const fetchActivityBundleDetails = async (
    id: string | number
  ): Promise<Datum> => {
    const response = await ActivityBundleAxiosInstance.get(
      `getActivityBundle/${id}`
    );

    if (response.data?.success === false) {
      throw new Error(
        response.data?.error || "Activity Bundle rule not found."
      );
    }

    const raw =
      response.data?.result ??
      response.data?.data?.result ??
      response.data?.data?.data ??
      response.data?.data ??
      response.data;

    const detail = Array.isArray(raw) ? raw[0] : raw;

    if (!detail || typeof detail !== "object") {
      throw new Error(
        "Activity Bundle details were not returned by the backend."
      );
    }

    const enrichedDetail = await enrichBundleDoctorSqlIds(detail);
    return normalizeItem(enrichedDetail);
  };

  const handleOpenDialog = async (item: Datum) => {
    try {
      const detail = await fetchActivityBundleDetails(item.id);
      setSelectedItem(detail);
    } catch (error: any) {
      // Backend GET-by-ID excludes soft-deleted rows.
      if ((item as any).is_deleted) {
        setSelectedItem(normalizeItem(item));
        return;
      }

      toast({
        title: "Error",
        description:
          error.response?.data?.error ||
          error.message ||
          "Failed to load Activity Bundle details.",
        variant: "destructive",
      });
    }
  };

  const handleAddClick = () => {
    setEditIndex(null);
    clear();
    setIsDialogOpen(true);
  };

  const handleEditClick = async (item: Datum) => {
    setClearMainActivityMedicalGroups(false);
    setClearMainActivityCustomGroups(false);
    setClearProvider(false);
    setClearDoctor(false);
    setClearNetworks(false);
    setClearInsuranceplan(false);
    setClearPatient(false);
    setClearPolicy(false);
    setClearVip(false);
    setClearSpecialty(false);

    try {
      const detail = await fetchActivityBundleDetails(item.id);
      handleEdit(detail);
      setIsDialogOpen(true);
    } catch (error: any) {
      toast({
        title: "Error",
        description:
          error.response?.data?.error ||
          error.message ||
          "Failed to load Activity Bundle details for editing.",
        variant: "destructive",
      });
    }
  };

  const hasMainActivitySelection = (): boolean =>
    flattenByType(mainActivitiesByType).length > 0 ||
    mainActivityMedicalGroups.length > 0 ||
    mainActivityCustomGroups.length > 0;

  const hasBundledActivitySelection = (): boolean =>
    activityGroups.some((group) =>
      flattenByType(group.activitiesByType).length > 0 ||
      group.medicalGroups.length > 0 ||
      group.customGroups.length > 0
    );

  const addItem = async (): Promise<void> => {
    if (!relationName.trim()) {
      toast({ title: "Error", description: "Relation Name is required.", variant: "destructive" }); return;
    }
    if (relationName.trim().length < 3) {
      toast({ title: "Error", description: "Relation Name must be at least 3 characters.", variant: "destructive" }); return;
    }
    if (!hasMainActivitySelection()) {
      toast({ title: "Validation Error", description: "Please select at least one Main Activity Code, Medical Group, or Custom Group.", variant: "destructive" });
      return;
    }
    if (!hasBundledActivitySelection()) {
      toast({ title: "Validation Error", description: "Please select at least one bundled Activity Code, Medical Group, or Custom Group.", variant: "destructive" });
      return;
    }
    const duplicate = findDuplicateActivity();
    if (duplicate) {
      toast({ title: "Duplicate activity is not allowed", description: `The same item exists in Activity ${duplicate.first} and Activity ${duplicate.second}.`, variant: "destructive" });
      return;
    }
    const entityError = validateEntities();
    if (entityError) { toast({ title: "Validation Error", description: entityError, variant: "destructive" }); return; }
    try {
      const response = await ActivityBundleAxiosInstance.post(
        "addNewActivityBundle",
        { data: [buildPayload()], token }
      );
      if (response.data.success) {
        await fetchItems();
        setCurrentPage(0);
        clear(); setIsDialogOpen(false);
        toast({ title: "Success", description: "New Activity Bundle Added Successfully." });
      } else {
        const errors = response.data.errors || [];
        toast({ title: "Error", description: errors.map((e: any) => e.error).join("\n") || "Failed to add.", variant: "destructive" });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  const updateItem = async (id: string | number): Promise<void> => {
    if (!relationName.trim()) {
      toast({ title: "Error", description: "Relation Name is required.", variant: "destructive" }); return;
    }
    if (relationName.trim().length < 3) {
      toast({ title: "Error", description: "Relation Name must be at least 3 characters.", variant: "destructive" }); return;
    }
    if (!hasMainActivitySelection()) {
      toast({ title: "Validation Error", description: "Please select at least one Main Activity Code, Medical Group, or Custom Group.", variant: "destructive" });
      return;
    }
    if (!hasBundledActivitySelection()) {
      toast({ title: "Validation Error", description: "Please select at least one bundled Activity Code, Medical Group, or Custom Group.", variant: "destructive" });
      return;
    }
    const duplicate = findDuplicateActivity();
    if (duplicate) {
      toast({ title: "Duplicate activity is not allowed", description: `The same item exists in Activity ${duplicate.first} and Activity ${duplicate.second}.`, variant: "destructive" });
      return;
    }
    const entityError = validateEntities();
    if (entityError) { toast({ title: "Validation Error", description: entityError, variant: "destructive" }); return; }
    try {
      const response = await ActivityBundleAxiosInstance.put(
        "updateActivityBundle",
        { data: { ...buildPayload(), id }, token }
      );
      if (response.data.success) {
        await fetchItems();
        clear(); setIsDialogOpen(false);
        toast({ title: "Success", description: "Activity Bundle updated successfully." });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  const buildPayloadFromExisting = (
    item: Datum,
    nextActive: boolean
  ) => {
    const normalized = normalizeItem(item) as any;
    const mainFlat: CodeWithType[] = normalized.main_activity_types || [];
    const activitiesFlat: CodeWithType[] =
      normalized.not_allowed_activity_types || [];

    return {
      id: Number(normalized.id),
      relation_name: String(normalized.relation_name ?? "").trim(),

      main_activity: mainFlat.map((entry) => String(entry.code)),
      main_activity_type: mainFlat.map((entry) =>
        String(entry.type ?? "CPT").toLowerCase()
      ),
      medical_groups_main: toNumericIds(
        normalized.main_activity_medicalgroups || []
      ),
      custom_groups_main: toNumericIds(
        normalized.main_activity_customgroups || []
      ),

      activities: activitiesFlat.map((entry) => String(entry.code)),
      activities_type: activitiesFlat.map((entry) =>
        String(entry.type ?? "CPT").toLowerCase()
      ),
      medical_groups_activities: toNumericIds(
        normalized.not_allowed_activity_medicalgroups || []
      ),
      custom_groups_activities: toNumericIds(
        normalized.not_allowed_activity_customgroups || []
      ),

      items_order: getOrderedItems(normalized),

      provider: normalized.provider_all
        ? []
        : toStringDbIds(normalized.provider || []),
      provider_mode: normalized.provider_mode || "DISABLED",
      provider_all: !!normalized.provider_all,

      network: normalized.network_all
        ? []
        : toNumericIds(normalized.networks ?? normalized.network ?? []),
      network_mode: normalized.network_mode || "DISABLED",
      network_all: !!normalized.network_all,

      policy: normalized.policy_all
        ? []
        : getPolicyIds(normalized.policy || []),
      policy_mode: normalized.policy_mode || "DISABLED",
      policy_all: !!normalized.policy_all,

      insuranceplan: normalized.insuranceplan_all
        ? []
        : toNumericIds(normalized.insuranceplan || []),
      insuranceplan_mode:
        normalized.insuranceplan_mode || "DISABLED",
      insuranceplan_all: !!normalized.insuranceplan_all,

      payer: normalized.payer_all ? [] : normalized.payer || [],
      payer_mode: normalized.payer_mode || "DISABLED",
      payer_all: !!normalized.payer_all,

      patient: normalized.patient_all
        ? []
        : toNumericIds(normalized.patients ?? normalized.patient ?? []),
      patient_mode: normalized.patient_mode || "DISABLED",
      patient_all: !!normalized.patient_all,

      user_level: normalized.user_all
        ? []
        : normalized.user_level || [],
      user_mode: normalized.user_mode || "DISABLED",
      user_all: !!normalized.user_all,

      vip: normalized.vip_all
        ? []
        : toNumericIds(normalized.vip || []),
      vip_mode: normalized.vip_mode || "DISABLED",
      vip_all: !!normalized.vip_all,

      doctor: normalized.doctor_all
        ? []
        : toStringDbIds(normalized.doctor || []),
      doctor_mode: normalized.doctor_mode || "DISABLED",
      doctor_all: !!normalized.doctor_all,

      specialty: normalized.specialty_all
        ? []
        : toNumericIds(normalized.specialty || []),
      specialty_mode: normalized.specialty_mode || "DISABLED",
      specialty_all: !!normalized.specialty_all,

      is_active: nextActive,
    };
  };

  const toggleActiveStatus = async (item: Datum) => {
    try {
      const detail = await fetchActivityBundleDetails(item.id);
      const payload = {
        data: buildPayloadFromExisting(detail, !detail.is_active),
        token,
      };

      const response = await ActivityBundleAxiosInstance.put(
        "updateActivityBundle",
        payload
      );

      if (response.data.success) {
        await fetchItems();
        toast({
          title: "Success",
          description: `Status changed to ${
            !detail.is_active ? "Active" : "Inactive"
          }.`,
        });
      } else {
        toast({
          title: "Error",
          description:
            response.data.error || "Failed to change Activity Bundle status.",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description:
          error.response?.data?.error ||
          error.message ||
          "An unexpected error occurred.",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (item: Datum) => {
    const normalized = normalizeItem(item);
    setRelationName(normalized.relation_name);
    setIsActive(normalized.is_active);
    setEditIndex(normalized.id);

    const mainFlat: CodeWithType[] = (normalized as any).main_activity_types || [];
    if (mainFlat.length > 0) { setMainActivitiesByType(groupByType(mainFlat)); setSelectedMainOption("list_of_Activity_code"); }
    else { setMainActivitiesByType({ ...EMPTY_BY_TYPE }); setSelectedMainOption(null); }

    const MainCustom = Array.isArray(normalized.main_activity_customgroups)
      ? normalized.main_activity_customgroups.map((obj: any) => typeof obj === "string" ? { id: 0, name: obj } : obj) : [];
    setMainActivityCustomGroups(MainCustom.map((o: any) => markReference(o, { value: o.id, label: o.name })));
    if (MainCustom.length > 0 && !mainFlat.length) setSelectedMainOption("custom_Activity_groups");

    const MainMedical = Array.isArray(normalized.main_activity_medicalgroups)
      ? normalized.main_activity_medicalgroups.map((obj: any) => typeof obj === "string" ? { id: 0, name: obj } : obj) : [];
    setMainActivityMedicalGroups(MainMedical.map((o: any) => markReference(o, { value: o.id, label: o.name })));
    if (MainMedical.length > 0 && !mainFlat.length) setSelectedMainOption("medical_Activity_groups");
    if (mainFlat.length > 0) setSelectedMainOption("list_of_Activity_code");

    const notAllowedFlat: CodeWithType[] = (normalized as any).not_allowed_activity_types || [];
    const NotAllowedCustom = Array.isArray(normalized.not_allowed_activity_customgroups)
      ? normalized.not_allowed_activity_customgroups.map((obj: any) =>
          typeof obj === "string" ? { id: 0, name: obj } : obj,
        )
      : [];
    const NotAllowedMedical = Array.isArray(normalized.not_allowed_activity_medicalgroups)
      ? normalized.not_allowed_activity_medicalgroups.map((obj: any) =>
          typeof obj === "string" ? { id: 0, name: obj } : obj,
        )
      : [];

    const loadedActivityGroups: ActivitySelectionGroup[] = [];
    const orderedItems = getOrderedItems(normalized);

    if (orderedItems.length > 0) {
      const orderNumbers = Array.from(new Set(orderedItems.map((entry) => entry.activity_order))).sort((a, b) => a - b);
      orderNumbers.forEach((orderNumber) => {
        const entries = orderedItems.filter((entry) => entry.activity_order === orderNumber);
        const codeEntries: CodeWithType[] = entries
          .filter((entry) => entry.type === "code")
          .map((entry) => ({ code: entry.code ?? "", type: normalizeActivityType(entry.activity_type) }));
        const medicalEntries = entries
          .filter((entry) => entry.type === "medicalgroup")
          .map((entry) => {
            const found = NotAllowedMedical.find((o: any) => String(o.id) === String(entry.id));
            return markReference(found ?? entry, { value: entry.id ?? "", label: entry.name ?? String(entry.id ?? "") });
          });
        const customEntries = entries
          .filter((entry) => entry.type === "customgroup")
          .map((entry) => {
            const found = NotAllowedCustom.find((o: any) => String(o.id) === String(entry.id));
            return markReference(found ?? entry, { value: entry.id ?? "", label: entry.name ?? String(entry.id ?? "") });
          });
        loadedActivityGroups.push(createActivitySelectionGroup({
          selectedOption: codeEntries.length > 0 ? "list_of_Activity" : medicalEntries.length > 0 ? "medical_Activity_groups" : customEntries.length > 0 ? "custom_Activity_groups" : null,
          activitiesByType: groupByType(codeEntries),
          medicalGroups: medicalEntries,
          customGroups: customEntries,
        }));
      });
    } else {
      if (notAllowedFlat.length > 0) {
        loadedActivityGroups.push(createActivitySelectionGroup({ selectedOption: "list_of_Activity", activitiesByType: groupByType(notAllowedFlat) }));
      }
      if (NotAllowedMedical.length > 0) {
        loadedActivityGroups.push(createActivitySelectionGroup({
          selectedOption: "medical_Activity_groups",
          medicalGroups: NotAllowedMedical.map((o: any) => markReference(o, { value: o.id, label: o.name })),
        }));
      }
      if (NotAllowedCustom.length > 0) {
        loadedActivityGroups.push(createActivitySelectionGroup({
          selectedOption: "custom_Activity_groups",
          customGroups: NotAllowedCustom.map((o: any) => markReference(o, { value: o.id, label: o.name })),
        }));
      }
    }

    setActivityGroups(
      loadedActivityGroups.length > 0
        ? loadedActivityGroups.map((group, index) => ({ ...group, isExpanded: index === 0 }))
        : [createActivitySelectionGroup({ isExpanded: true })],
    );

    const toArr = (arr: any[], key: string) =>
      (Array.isArray(arr) ? arr.map((o: any) => typeof o === "string" ? { id: 0, [key]: o } : o) : []);

    const mappedNetworks = toArr(
      (normalized.networks ?? (normalized as any).network ?? []),
      "Network_name"
    ).map((o: any) => markReference(o, {
      value: o.id ?? o.dbId ?? o.db_id,
      label: formatSqlEntityLabel(
        o,
        o.Network_name,
        o.network_name,
        o.name
      ),
    }));
    setNetworks(mappedNetworks);
    setNetworkMode(modeFromString(normalized.network_mode));
    setIsNetworkActive(normalized.network_mode === "INCLUDED" || normalized.network_mode === "EXCLUDED");
    setSelectAllNetworks(normalized.network_all ?? false);

    const mappedInsurance = toArr(normalized.insuranceplan || [], "Plan_name")
      .map((o: any) => markReference(o, {
        value: o.id ?? o.dbId ?? o.db_id,
        label: formatSqlEntityLabel(o, o.Plan_name, o.plan_name, o.name),
      }));
    setInsuranceplan(mappedInsurance);
    setInsuranceMode(modeFromString(normalized.insuranceplan_mode));
    setIsInsuranceActive(normalized.insuranceplan_mode === "INCLUDED" || normalized.insuranceplan_mode === "EXCLUDED");
    setSelectAllInsurance(normalized.insuranceplan_all ?? false);

    const mappedProvider = toArr(normalized.provider || [], "Provider_name")
      .map((o: any) => markReference(o, {
        value: o.id ?? o.dbId ?? o.db_id ?? o.Provider_id,
        label: formatBusinessEntityLabel(
          o,
          o.Provider_id ?? o.provider_id ?? o.providerId,
          o.Provider_name,
          o.provider_name,
          o.providerName,
          o.name
        ),
        dbId: Number(o.id ?? o.dbId ?? o.db_id ?? 0) || undefined,
        providerId: o.Provider_id ?? o.provider_id ?? "",
        providerName:
          o.Provider_name ??
          o.provider_name ??
          o.providerName ??
          o.name ??
          "",
      }));
    setProvider(mappedProvider);
    setProviderMode(modeFromString(normalized.provider_mode));
    setIsProviderActive(normalized.provider_mode === "INCLUDED" || normalized.provider_mode === "EXCLUDED");
    setSelectAllProviders(normalized.provider_all ?? false);

const mappedDoctor = toArr(normalized.doctor || [], "Doctor_name")
      .map((o: any) => markReference(o, {
        value: o.id ?? o.dbId ?? o.db_id ?? o.Doctor_id,
        label: formatBusinessEntityLabel(
          o,
          o.Doctor_id ?? o.doctor_id ?? o.doctorId,
          o.Doctor_name,
          o.doctor_name,
          o.doctorName,
          o.name
        ),
        dbId: Number(o.id ?? o.dbId ?? o.db_id ?? 0) || undefined,
        doctorId: o.Doctor_id ?? o.doctor_id ?? "",
        Doctor_id: o.Doctor_id ?? o.doctor_id ?? "",
        doctorName:
          o.Doctor_name ??
          o.doctor_name ??
          o.doctorName ??
          o.name ??
          "",
        Doctor_name:
          o.Doctor_name ??
          o.doctor_name ??
          o.doctorName ??
          o.name ??
          "",
      }));
    setDoctor(mappedDoctor);
    setDoctorMode(modeFromString(normalized.doctor_mode));
    setIsDoctorActive(normalized.doctor_mode === "INCLUDED" || normalized.doctor_mode === "EXCLUDED");
    setSelectAllDoctor(normalized.doctor_all ?? false);

    const mappedPatients = toArr(
      (normalized.patients ?? (normalized as any).patient ?? []),
      "name"
    ).map((o: any) => markReference(o, {
      value: o.id ?? o.dbId ?? o.db_id,
      label: formatBundlePatientLabel(o),
      dbId: Number(o.id ?? o.dbId ?? o.db_id ?? 0) || undefined,
      patientName:
        o.full_name ??
        o.Full_name ??
        o.name ??
        "",
      nationalId: o.National_id ?? o.national_id ?? "",
      memberId: o.Member_id ?? o.member_id ?? "",
      patientPassport:
        o.Patient_passport ??
        o.patient_passport ??
        o.passport ??
        "",
    }));
    setPatients(mappedPatients);
    setPatientMode(modeFromString(normalized.patient_mode));
    setIsPatientActive(normalized.patient_mode === "INCLUDED" || normalized.patient_mode === "EXCLUDED");
    setSelectAllPatients(normalized.patient_all ?? false);

    const mappedVip = toArr(normalized.vip || [], "name")
      .map((o: any) => markReference(o, {
        value: o.id ?? o.dbId ?? o.db_id,
        label: formatSqlEntityLabel(o, o.name, o.Vip_name, o.vip_name),
      }));
    setVip(mappedVip);
    setVipMode(modeFromString(normalized.vip_mode));
    setIsVipActive(normalized.vip_mode === "INCLUDED" || normalized.vip_mode === "EXCLUDED");
    setSelectAllVip(normalized.vip_all ?? false);

    const mappedPolicy = Array.isArray(normalized.policy)
      ? normalized.policy.map((obj: any) => markReference(obj, { id: obj.id || 0, Policy_id: obj.Policy_id || "", Policy_name: obj.Policy_name || "", plans: obj.plans || [] })) : [];
    setPolicy(mappedPolicy);
    setPolicyMode(modeFromString(normalized.policy_mode));
    setIsPolicyActive(normalized.policy_mode === "INCLUDED" || normalized.policy_mode === "EXCLUDED");
    setSelectAllPolicy(normalized.policy_all ?? false);

    const mappedPayer = normalized.payer || [];
    setPayer(mappedPayer);
    setPayerMode(modeFromString(normalized.payer_mode));
    setIsPayerActive(normalized.payer_mode === "INCLUDED" || normalized.payer_mode === "EXCLUDED");
    setSelectAllPayer(normalized.payer_all ?? false);

    const mappedUser = normalized.user_level || [];
    setUserLevel(mappedUser);
    setUserMode(modeFromString(normalized.user_mode));
    setIsUserActive(normalized.user_mode === "INCLUDED" || normalized.user_mode === "EXCLUDED");
    setSelectAllUser(normalized.user_all ?? false);

    const mappedSpecialty = toArr(normalized.specialty || [], "name")
      .map((o: any) => markReference(o, {
        value: o.id ?? o.dbId ?? o.db_id,
        label: formatBusinessEntityLabel(
          o,
          o.speciality_code ?? o.specialty_code ?? o.Speciality_code ?? o.Specialty_code,
          o.name,
          o.Specialty_name,
          o.specialty_name,
          o.speciality_name
        ),
      }));
    setSpecialty(mappedSpecialty);
    if (mappedSpecialty.length > 0 || normalized.specialty_all) {
      const spMode = normalized.specialty_mode === "EXCLUDED" ? "EXCLUSION"
        : normalized.specialty_mode === "INCLUDED" ? "INCLUSION" : null;
      setSpecialtyMode(spMode);
      setIsSpecialtyActive(true);
    } else {
      setSpecialtyMode(null);
      setIsSpecialtyActive(false);
    }
    setSelectAllSpecialty(normalized.specialty_all ?? false);


const hasEntities = !!(
  mappedProvider.length || mappedDoctor.length || mappedNetworks.length ||
  mappedInsurance.length || mappedPolicy.length || mappedPatients.length ||
  mappedVip.length || mappedPayer.length || mappedUser.length || mappedSpecialty.length ||
  normalized.provider_all || normalized.doctor_all || normalized.network_all ||
  normalized.insuranceplan_all || normalized.policy_all || normalized.patient_all ||
  normalized.vip_all || normalized.payer_all || normalized.user_all || normalized.specialty_all
);
setShowEntities(hasEntities);
  };

  // ── Soft Delete ───────────────────────────────────────────────────────────
  const removeItem = async (id: string | number): Promise<void> => {
    try {
      const response = await ActivityBundleAxiosInstance.delete(
        `softDeleteActivityBundle/${id}`,
        { data: { token } }
      );

      if (response.data.success) {
        await fetchItems();
        toast({
          title: "Success",
          description: "Activity Bundle deleted successfully.",
        });
      } else {
        toast({
          title: "Error",
          description: response.data.error || "Failed to delete Activity Bundle.",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description:
          error.response?.data?.error || "Failed to delete Activity Bundle.",
        variant: "destructive",
      });
    }
  };

  // ── Restore ───────────────────────────────────────────────────────────────
  const restoreItem = async (id: string | number): Promise<void> => {
    try {
      const response = await ActivityBundleAxiosInstance.patch(
        `restoreActivityBundle/${id}`,
        { token }
      );

      if (response.data.success) {
        await fetchItems();
        toast({
          title: "Success",
          description: "Activity Bundle restored successfully.",
        });
      } else {
        toast({
          title: "Error",
          description: response.data.error || "Failed to restore Activity Bundle.",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description:
          error.response?.data?.error || "Failed to restore Activity Bundle.",
        variant: "destructive",
      });
    }
  };

  // ── Hard Delete (permanent) ───────────────────────────────────────────────
  const permanentDeleteItem = async (
    id: string | number
  ): Promise<void> => {
    try {
      const response = await ActivityBundleAxiosInstance.delete(
        `deleteActivityBundle/${id}`,
        { data: { token } }
      );

      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false);
        setForceId(null);
        setForceError("");
        setForceAction(null);
        toast({
          title: "Success",
          description: "Activity Bundle permanently deleted.",
        });
      } else {
        setForceError(
          response.data.error || "Failed to permanently delete Activity Bundle."
        );
      }
    } catch (error: any) {
      setForceError(
        error.response?.data?.error ||
          "Failed to permanently delete Activity Bundle."
      );
    }
  };

  const handleDownload = (item: Datum) => {
    const mainFlat: CodeWithType[] = (item as any).main_activity_types || [];
    const notAllowedFlat: CodeWithType[] = (item as any).not_allowed_activity_types || [];
    const rows = ["relation_name;main_activity_code;main_activity_type;not_allowed_activity_code;not_allowed_activity_type;provider;apply_to_other_providers;networks;apply_to_other_networks;insuranceplan;apply_to_other_insuranceplan;payer;apply_to_other_payer;patients;apply_to_other_patients;policy;apply_to_other_policy;user_level;apply_to_other_user;doctor;apply_to_other_doctor;vip;apply_to_vip;specialty;specialty_mode;is_active"];
    const specialtyArr = item.specialty || [];
    const maxLen = Math.max(mainFlat.length, notAllowedFlat.length,
      (item.provider || []).length, (item.networks || []).length, (item.insuranceplan || []).length,
      (item.patients || []).length, (item.policy || []).length, (item.user_level || []).length,
      (item.doctor || []).length, (item.payer || []).length, (item.vip || []).length,
      specialtyArr.length, 1);
    for (let i = 0; i < maxLen; i++) {
      rows.push([
        item.relation_name || "",
        i < mainFlat.length ? mainFlat[i].code : "",
        i < mainFlat.length ? mainFlat[i].type : "",
        i < notAllowedFlat.length ? notAllowedFlat[i].code : "",
        i < notAllowedFlat.length ? notAllowedFlat[i].type : "",
        i < (item.provider || []).length ? (item.provider![i] as any).Provider_name || "" : "",
        item.apply_to_other_providers ? "Exclude" : "Include",
        i < (item.networks || []).length ? (item.networks![i] as any).network_name || "" : "",
        item.apply_to_other_networks ? "Exclude" : "Include",
        i < (item.insuranceplan || []).length ? (item.insuranceplan![i] as any).Plan_name || "" : "",
        item.apply_to_other_insuranceplan ? "Exclude" : "Include",
        i < (item.payer || []).length ? item.payer![i] : "",
        item.apply_to_other_payer ? "Exclude" : "Include",
        i < (item.patients || []).length ? (item.patients![i] as any).name || "" : "",
        item.apply_to_other_patients ? "Exclude" : "Include",
        i < (item.policy || []).length ? (item.policy![i] as any).Policy_name || "" : "",
        item.apply_to_other_policy ? "Exclude" : "Include",
        i < (item.user_level || []).length ? item.user_level![i] : "",
        item.apply_to_other_user ? "Exclude" : "Include",
        i < (item.doctor || []).length ? (item.doctor![i] as any).Doctor_name || "" : "",
        item.apply_to_other_doctor ? "Exclude" : "Include",
        i < (item.vip || []).length ? (item.vip![i] as any).name || "" : "",
        item.apply_to_vip ? "Exclude" : "Include",
        i < specialtyArr.length ? (specialtyArr[i] as any).name || "" : "",
        item.specialty_mode ?? "DISABLED",
        item.is_active ? "TRUE" : "FALSE",
      ].join(";"));
    }
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `ActivityNotAllowedIncludeActivity${item.id}.csv`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const isRelationRestricted = (item: any): boolean => {
    if (!item) return false;
    const allGroups = [
      ...(Array.isArray(item.main_activity_medicalgroups) ? item.main_activity_medicalgroups : []),
      ...(Array.isArray(item.main_activity_customgroups) ? item.main_activity_customgroups : []),
      ...(Array.isArray(item.not_allowed_activity_medicalgroups) ? item.not_allowed_activity_medicalgroups : []),
      ...(Array.isArray(item.not_allowed_activity_customgroups) ? item.not_allowed_activity_customgroups : []),
      ...(Array.isArray(item.medical_groups) ? item.medical_groups : []),
      ...(Array.isArray(item.custom_groups) ? item.custom_groups : []),
    ];
    return allGroups.some((g: any) => {
      if (!g) return false;
      if (typeof g === "object") {
        if (g.is_active === false) return true;
        if (typeof g.status === "string" && g.status.toLowerCase() === "inactive") return true;
      }
      return false;
    });
  };

  const columns: ColumnDef<Datum>[] = [
    {
      accessorKey: "id",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          ID {column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
    cell: ({ row }) => (
      <div className="text-center w-full font-medium">
        {currentPage * pageSize + row.index + 1}
      </div>
    ),
    },
    {
      accessorKey: "relation_name",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Relation Name {column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const v = row.getValue("relation_name") as any;
        const isDeleted = (row.original as any).is_deleted;
        return (
          <div className={`w-full text-center ${isDeleted ? "line-through text-gray-400" : ""}`}>
            {Array.isArray(v) ? v.join(", ") : v}
          </div>
        );
      },
    },
    {
      accessorKey: "main_activity",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Main Activity {column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        const mainFlat: CodeWithType[] = (item as any).main_activity_types || [];
        const customNames = Array.isArray(item.main_activity_customgroups)
          ? item.main_activity_customgroups.map((g: any) => `[Custom] ${g.name || g}`) : [];
        const medicalNames = Array.isArray(item.main_activity_medicalgroups)
          ? item.main_activity_medicalgroups.map((g: any) => `[Medical] ${g.name || g}`) : [];
        const groups = [...medicalNames, ...customNames];
        return (
          <ActivityCell items={mainFlat} groups={groups} searchTerm={filterSearch}
            onShowAll={(its, grps) => {
              setAllCodesDialogItems(its); setAllCodesDialogGroups(grps);
              setAllCodesDialogTitle("Main Activity – All codes"); setAllCodesDialogOpen(true);
            }}
          />
        );
      },
    },
    {
      accessorKey: "not_allowed_activity",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Activities {column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        const ordered = getOrderedItems(item);
        if (ordered.length > 0) {
          const groups = Array.from(new Set(ordered.map((entry) => entry.activity_order))).sort((a, b) => a - b);
          return (
            <div className="w-full space-y-2 px-2 py-2">
              {groups.map((order) => {
                const entries = ordered.filter((entry) => entry.activity_order === order);
                return (
                  <div key={order} className="rounded-lg border border-slate-200 bg-slate-50 p-2">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10px] font-bold text-indigo-700">Activity {order}</span>
                      <span className="text-[10px] text-slate-500">{entries.length} item{entries.length !== 1 ? "s" : ""}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {entries.slice(0, 3).map((entry, index) => {
                        const activityType = normalizeActivityType(entry.activity_type);
                        const colors = entry.type === "code" ? getActivityTypeColor(activityType) : entry.type === "medicalgroup"
                          ? { bg: "bg-green-50", border: "border-green-300", text: "text-green-800" }
                          : { bg: "bg-purple-50", border: "border-purple-300", text: "text-purple-800" };
                        const label = entry.type === "code" ? String(entry.code) : String(entry.name ?? entry.id);
                        const typeLabel = entry.type === "code" ? activityType : entry.type === "medicalgroup" ? "Medical" : "Custom";
                        return <span key={`${orderedItemKey(entry)}-${index}`} className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-[11px] font-semibold ${colors.bg} ${colors.border} ${colors.text}`}>{label}<small className="text-[9px] uppercase opacity-70">{typeLabel}</small></span>;
                      })}
                      {entries.length > 3 && <span className="px-2 py-1 text-[11px] font-semibold text-blue-700">+{entries.length - 3} more</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        }
        const notAllowedFlat: CodeWithType[] = (item as any).not_allowed_activity_types || [];
        const customNames = Array.isArray(item.not_allowed_activity_customgroups)
          ? item.not_allowed_activity_customgroups.map((g: any) => `[Custom] ${g.name || g}`) : [];
        const medicalNames = Array.isArray(item.not_allowed_activity_medicalgroups)
          ? item.not_allowed_activity_medicalgroups.map((g: any) => `[Medical] ${g.name || g}`) : [];
        const groups = [...medicalNames, ...customNames];
        return <ActivityCell items={notAllowedFlat} groups={groups} searchTerm={filterSearch} onShowAll={(its, grps) => { setAllCodesDialogItems(its); setAllCodesDialogGroups(grps ?? []); setAllCodesDialogTitle("Activities – All codes"); setAllCodesDialogOpen(true); }} />;
      },
    },
    {
      accessorKey: "is_active",
      enableSorting: false,
      header: () => <div className="w-full text-center normal-case font-normal">Status</div>,
      cell: ({ row }) => {
        const item = row.original;
        const isDeleted = (item as any).is_deleted;
        return (
          <div className="w-full flex justify-center">
            <button
              onClick={() => !isDeleted && toggleActiveStatus(item)}
              disabled={!!isDeleted}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isDeleted ? "opacity-40 cursor-not-allowed bg-gray-200" : item.is_active ? "bg-green-500" : "bg-gray-300"}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${item.is_active && !isDeleted ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
        );
      },
    },
    {
      id: "view",
      header: () => <div className="w-full text-center">View</div>,
      cell: ({ row }) => (
        <div className="w-full flex justify-center py-2">
          <button onClick={() => handleOpenDialog(row.original)} className="text-blue-600 hover:text-blue-800" title="View Details">
            <i className="fa-solid fa-eye text-lg"></i>
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="h5-container mx-auto px-1 pt-16 pb-4">
      <div className="flex justify-between items-center">
        <h5 className="mb-6 flex items-center gap-2 font-bold text-2xl mt-4 text-blue-900">
          <span className="icon-container flex items-center justify-center w-10 h-10 rounded-full bg-blue-900 text-white">
            <i className="fa-solid fa-layer-group"></i>
          </span>
          Activity Bundle
        </h5>
      </div>

      <DataTable2
        columns={columns}
        isRowRestricted={isRelationRestricted}
        data={filteredData}
        searchColumns={[]}
        validateButtonLabel="Validate"
        addButtonLabel="Add New Relation"
        showAddButton={false}
        pageIndex={currentPage}
        onPageChange={setCurrentPage}
        manualPagination={manualPagination}
        pageCount={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageSizeChange={(nextPageSize) => {
          setPageSize(nextPageSize);
          setCurrentPage(0);
        }}
        allowedPageSizes={allowedPageSizes}
        isLoading={isTableLoading}
        onEdit={handleEdit}
        onDelete={(id) => {
          const item = items.data.find(i => i.id === id);
          if ((item as any)?.is_deleted) {
            setForceId(id);
            setForceAction("hardDelete");
            setForceError("");
            setForceDialogOpen(true);
          } else {
            removeItem(id);
          }
        }}
        onRestore={canManageDeleted ? restoreItem : undefined}
canHardDelete={canManageDeleted}
        onAdd={handleAddClick}
        onEditClick={handleEditClick}
        onDownload={handleDownload}
        isRowDeleted={(row: any) => row.is_deleted === true}
        toolbarExtras={
          <div style={{ width: "100%", position: "relative" }}>
<div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "nowrap", padding: "10px 160px 10px 16px", background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)", borderRadius: 12, border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.04)", marginBottom: 12 }}>
  <ServerDeletedPills
    value={deletedFilter}
    onChange={(value) => { setDeletedFilter(value); setCurrentPage(0); }}
    />
              <button type="button" onClick={handleAddClick} style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 18px", fontSize: 13, fontWeight: 600, color: "#ffffff", background: "#1e3a8a", border: "none", borderRadius: 8, cursor: "pointer", whiteSpace: "nowrap", boxShadow: "0 2px 6px rgba(30,58,138,0.25)" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg>
                Add New Relation
              </button>
              <div style={{ width: 1, height: 28, background: "#cbd5e1" }} />
              <UnifiedSearchBox
                value={filterSearch}
                onChange={(value) => { setFilterSearch(value); setCurrentPage(0); }}
                helpText="Relation Name, Main Activity, Activities, Medical Group, or Custom Group."
              />
              <div style={{ width: 1, height: 28, background: "#cbd5e1" }} />
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b", letterSpacing: "0.05em" }}>Status</span>
                <div style={{ display: "flex", alignItems: "center", padding: 4, background: "#e2e8f0", borderRadius: 9999 }}>
                  {[{ value: "", label: "All" }, { value: "true", label: "Active" }, { value: "false", label: "Inactive" }].map((option) => (
                    <button key={option.value} type="button" onClick={() => setFilterStatus(option.value)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", fontSize: 13, fontWeight: 500, borderRadius: 9999, border: "none", cursor: "pointer", background: filterStatus === option.value ? option.value === "true" ? "#10b981" : option.value === "false" ? "#94a3b8" : "#ffffff" : "transparent", color: filterStatus === option.value ? option.value === "" ? "#334155" : "#ffffff" : "#64748b", boxShadow: filterStatus === option.value ? "0 2px 4px rgba(0,0,0,0.1)" : "none" }}>
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ position: "absolute", top: 0, right: 16, height: 62, display: "flex", alignItems: "center" }}>
              <FileUpload fetchItems={fetchItems} />
            </div>
          </div>
        }
      />

      {/* ══ VIEW DIALOG ══════════════════════════════════════════════════════ */}
      <Dialog open={Boolean(selectedItem)} onOpenChange={(open) => { if (!open) setSelectedItem(null); }}>
        <DialogContent className="max-w-[940px] max-h-[92vh] flex flex-col p-0 overflow-hidden bg-white border-none rounded-2xl" style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)" }}>
          {selectedItem && (
            <>
              <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)", padding: "24px 28px 20px", flexShrink: 0, position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", top: -40, right: -40, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
                <div style={{ position: "absolute", bottom: -30, left: -20, width: 100, height: 100, borderRadius: "50%", background: "rgba(255,255,255,0.03)" }} />
                <div style={{ display: "flex", alignItems: "flex-start", gap: 16, position: "relative", zIndex: 1 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 14, background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, color: "#fff", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)" }}>
                    <i className="fa-solid fa-code-branch" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 2 }}>
                      <h2 style={{ fontSize: 20, fontWeight: 700, color: "#fff", margin: 0, letterSpacing: "-0.01em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{selectedItem.relation_name}</h2>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, flexShrink: 0, background: selectedItem.is_active ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.15)", color: selectedItem.is_active ? "#86efac" : "#fca5a5", border: `1px solid ${selectedItem.is_active ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.2)"}` }}>
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} />
                        {selectedItem.is_active ? "Active" : "Inactive"}
                      </span>
                      {(selectedItem as any).is_deleted && (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, flexShrink: 0, background: "rgba(239,68,68,0.2)", color: "#fca5a5", border: "1px solid rgba(239,68,68,0.3)" }}>
                          <i className="fa-solid fa-trash" style={{ fontSize: 10 }} /> Deleted
                        </span>
                      )}
                    </div>
                    <p style={{ fontSize: 12, color: "rgba(147,197,253,0.8)", margin: 0 }}>Rule ID: <span style={{ fontWeight: 600, color: "#93c5fd" }}>#{selectedItem.id}</span></p>
                  </div>
                </div>
              </div>

              <div style={{ overflowY: "auto", flex: 1, background: "#f8fafc", padding: "6px 0 16px" }}>
                {/* Activity Configuration */}
                <div style={{ padding: "20px 28px 16px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #dbeafe" }}>
                      <i className="fa-solid fa-code-branch" style={{ fontSize: 12, color: "#3b82f6" }} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Activity Configuration</span>
                  </div>
                  {(() => {
                    const typeColors: Record<string, { bg: string; color: string; border: string; dot: string }> = {
                      CPT:    { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe", dot: "#3b82f6" },
                      HCPCS:  { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0", dot: "#22c55e" },
                      Dental: { bg: "#faf5ff", color: "#7e22ce", border: "#e9d5ff", dot: "#a855f7" },
                      Drugs:  { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa", dot: "#f97316" },
                    };
                    const renderCodesBlock = (flat: CodeWithType[], medGroups: any[], customGroups: any[]) => {
                      const grouped: Record<string, string[]> = { CPT: [], HCPCS: [], Dental: [], Drugs: [] };
                      flat.forEach(({ code, type }) => { if (grouped[type]) grouped[type].push(String(code)); });
                      return (
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                          {flat.length > 0 && (
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                              {Object.entries(grouped).map(([type, codes]) => {
                                if (codes.length === 0) return null;
                                const tc = typeColors[type];
                                return (
                                  <div key={type} style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                                      <span style={{ width: 6, height: 6, borderRadius: 2, background: tc.dot, flexShrink: 0 }} />
                                      <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b" }}>{type} Codes</span>
                                      <span style={{ fontSize: 10, color: "#94a3b8", marginLeft: "auto" }}>{codes.length} items</span>
                                    </div>
                                    <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 5 }}>
                                      {codes.map((code, idx) => (<span key={idx} style={{ padding: "3px 10px", borderRadius: 9999, fontSize: 11, fontWeight: 500, background: tc.bg, color: tc.color, border: `0.5px solid ${tc.border}` }}>{code}</span>))}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                            {[
                              { label: "Medical Activity Groups", items: medGroups.map((g: any) => g.name || g), dot: "#22c55e", tagBg: "#f0fdf4", tagColor: "#15803d", tagBorder: "#bbf7d0" },
                              { label: "Custom Activity Groups",  items: customGroups.map((g: any) => g.name || g), dot: "#a855f7", tagBg: "#faf5ff", tagColor: "#7e22ce", tagBorder: "#e9d5ff" },
                            ].map((section) => (
                              <div key={section.label} style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                                  <span style={{ width: 6, height: 6, borderRadius: 2, background: section.dot, flexShrink: 0 }} />
                                  <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b" }}>{section.label}</span>
                                </div>
                                <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 5 }}>
                                  {section.items.length > 0
                                    ? section.items.map((item, i) => <span key={i} style={{ padding: "3px 10px", borderRadius: 9999, fontSize: 11, fontWeight: 500, background: section.tagBg, color: section.tagColor, border: `0.5px solid ${section.tagBorder}` }}>{item}</span>)
                                    : <span style={{ color: "#94a3b8", fontSize: 12, fontStyle: "italic" }}>None</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                          {flat.length === 0 && medGroups.length === 0 && customGroups.length === 0 && (<span style={{ color: "#94a3b8", fontSize: 12, fontStyle: "italic" }}>No data</span>)}
                        </div>
                      );
                    };
                    const mainFlat: CodeWithType[] = (selectedItem as any).main_activity_types || [];
                    const notAllowedFlat: CodeWithType[] = (selectedItem as any).not_allowed_activity_types || [];
                    return (
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                        <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: "16px 18px", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 14, paddingBottom: 12, borderBottom: "1px solid #f1f5f9" }}>
                            <div style={{ width: 24, height: 24, borderRadius: 6, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <i className="fa-solid fa-arrow-right" style={{ fontSize: 10, color: "#3b82f6" }} />
                            </div>
                            <span style={{ fontSize: 12, fontWeight: 700, color: "#1e293b" }}>Main Activity</span>
                            <span style={{ marginLeft: "auto", fontSize: 11, color: "#94a3b8" }}>{mainFlat.length} codes</span>
                          </div>
                          {renderCodesBlock(mainFlat, selectedItem.main_activity_medicalgroups || [], selectedItem.main_activity_customgroups || [])}
                        </div>
                        <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: "16px 18px", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 14, paddingBottom: 12, borderBottom: "1px solid #f1f5f9" }}>
                            <div style={{ width: 24, height: 24, borderRadius: 6, background: "#fff7ed", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <i className="fa-solid fa-ban" style={{ fontSize: 10, color: "#f97316" }} />
                            </div>
                            <span style={{ fontSize: 12, fontWeight: 700, color: "#1e293b" }}>Activities</span>
                            <span style={{ marginLeft: "auto", fontSize: 11, color: "#94a3b8" }}>{notAllowedFlat.length} codes</span>
                          </div>
                          {getOrderedItems(selectedItem).length > 0 ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                              {Array.from(new Set(getOrderedItems(selectedItem).map((entry) => entry.activity_order))).sort((a, b) => a - b).map((order) => {
                                const entries = getOrderedItems(selectedItem).filter((entry) => entry.activity_order === order);
                                return (
                                  <div key={order} style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 10, background: "#f8fafc" }}>
                                    <div style={{ marginBottom: 8 }}><span style={{ padding: "3px 9px", borderRadius: 9999, fontSize: 10, fontWeight: 700, color: "#4338ca", background: "#eef2ff", border: "1px solid #c7d2fe" }}>Activity {order}</span></div>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                      {entries.map((entry, index) => {
                                        const type = normalizeActivityType(entry.activity_type);
                                        const palette = entry.type === "code"
                                          ? ({ CPT: ["#eff6ff", "#1d4ed8", "#bfdbfe"], HCPCS: ["#f0fdf4", "#15803d", "#bbf7d0"], Dental: ["#faf5ff", "#7e22ce", "#e9d5ff"], Drugs: ["#fff7ed", "#c2410c", "#fed7aa"] } as any)[type]
                                          : entry.type === "medicalgroup" ? ["#f0fdf4", "#15803d", "#86efac"] : ["#faf5ff", "#7e22ce", "#d8b4fe"];
                                        const label = entry.type === "code" ? String(entry.code) : String(entry.name ?? entry.id);
                                        const typeLabel = entry.type === "code" ? type : entry.type === "medicalgroup" ? "Medical" : "Custom";
                                        return <span key={`${orderedItemKey(entry)}-${index}`} style={{ padding: "4px 9px", borderRadius: 7, fontSize: 11, fontWeight: 600, background: palette[0], color: palette[1], border: `1px solid ${palette[2]}` }}>{label} <small style={{ opacity: 0.7 }}>{typeLabel}</small></span>;
                                      })}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : renderCodesBlock(notAllowedFlat, selectedItem.not_allowed_activity_medicalgroups || [], selectedItem.not_allowed_activity_customgroups || [])}
                        </div>
                      </div>
                    );
                  })()}
                </div>

                <div style={{ height: 1, background: "#e2e8f0", margin: "0 28px" }} />

                {/* Target Scope */}
                <div style={{ padding: "20px 28px 16px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #fde68a" }}>
                      <i className="fa-solid fa-bullseye" style={{ fontSize: 12, color: "#d97706" }} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Target Scope & Visibility</span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                {[
  { label: "Providers",       icon: "fa-hospital",           color: "#3b82f6", mode: selectedItem.provider_mode,      items: (selectedItem.provider || []).map((p: any) => statusTag(p, formatBusinessEntityLabel(p, p?.Provider_id ?? p?.provider_id ?? p?.providerId, p?.Provider_name, p?.provider_name, p?.providerName, p?.name))).filter(Boolean), exclude: selectedItem.apply_to_other_providers, selectAll: selectedItem.select_all_providers },
  { label: "Doctors",         icon: "fa-stethoscope",        color: "#06b6d4", mode: selectedItem.doctor_mode,        items: (selectedItem.doctor || []).map((d: any) => statusTag(d, formatBusinessEntityLabel(d, d?.Doctor_id ?? d?.doctor_id ?? d?.doctorId, d?.Doctor_name, d?.doctor_name, d?.doctorName, d?.name))).filter(Boolean), exclude: selectedItem.apply_to_other_doctor, selectAll: selectedItem.select_all_doctor },
  { label: "Networks",        icon: "fa-network-wired",      color: "#8b5cf6", mode: selectedItem.network_mode,       items: (selectedItem.networks || []).map((n: any) => statusTag(n, formatSqlEntityLabel(n, n?.Network_name, n?.network_name, n?.name))).filter(Boolean), exclude: selectedItem.apply_to_other_networks, selectAll: selectedItem.select_all_networks },
  { label: "Insurance Plans", icon: "fa-shield-halved",      color: "#f59e0b", mode: selectedItem.insuranceplan_mode, items: (selectedItem.insuranceplan || []).map((i: any) => statusTag(i, formatSqlEntityLabel(i, i?.Plan_name, i?.plan_name, i?.name))).filter(Boolean), exclude: selectedItem.apply_to_other_insuranceplan, selectAll: selectedItem.select_all_insuranceplan },
  { label: "Policies",        icon: "fa-file-lines",         color: "#10b981", mode: selectedItem.policy_mode,        items: (selectedItem.policy || []).map((p: any) => statusTag(p, formatBundlePolicyLabel(p))).filter(Boolean), exclude: selectedItem.apply_to_other_policy, selectAll: selectedItem.select_all_policy },
  { label: "Patients",        icon: "fa-users",              color: "#ec4899", mode: selectedItem.patient_mode,       items: (selectedItem.patients || []).map((p: any) => statusTag(p, formatBundlePatientLabel(p))).filter(Boolean), exclude: selectedItem.apply_to_other_patients, selectAll: selectedItem.select_all_patients },
  { label: "VIP",             icon: "fa-star",               color: "#eab308", mode: selectedItem.vip_mode,           items: (selectedItem.vip || []).map((v: any) => statusTag(v, formatSqlEntityLabel(v, v?.name, v?.Vip_name, v?.vip_name))).filter(Boolean), exclude: selectedItem.apply_to_vip, selectAll: selectedItem.select_all_vip },
  { label: "Payer",           icon: "fa-building-columns",   color: "#6366f1", mode: selectedItem.payer_mode,         items: (selectedItem.payer || []).map((p: any) => String(p)),                                     exclude: selectedItem.apply_to_other_payer,         selectAll: selectedItem.select_all_payer },
  { label: "User Levels",     icon: "fa-user-gear",          color: "#64748b", mode: selectedItem.user_mode,          items: (selectedItem.user_level || []).map((u: any) => String(u)),                                exclude: selectedItem.apply_to_other_user,          selectAll: selectedItem.select_all_user },
  { label: "Specialty",       icon: "fa-briefcase-medical",  color: "#0d9488", mode: selectedItem.specialty_mode,     items: (selectedItem.specialty || []).map((s: any) => statusTag(s, formatBusinessEntityLabel(s, s?.speciality_code ?? s?.specialty_code ?? s?.Speciality_code ?? s?.Specialty_code, s?.name, s?.Specialty_name, s?.specialty_name, s?.speciality_name))).filter(Boolean), exclude: selectedItem.specialty_mode === "EXCLUDED", selectAll: selectedItem.specialty_all },
].map((e) => (
  <div key={e.label} style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", display: "flex", flexDirection: "column" as const }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
        <div style={{ width: 24, height: 24, borderRadius: 6, background: `${e.color}12`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <i className={`fa-solid ${e.icon}`} style={{ fontSize: 10, color: e.color }} />
        </div>
        <span style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>{e.label}</span>
      </div>
      {e.mode && e.mode !== "DISABLED" && (
        <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 9999, fontWeight: 600, background: e.exclude ? "#fef2f2" : "#f0fdf4", color: e.exclude ? "#dc2626" : "#15803d", border: `1px solid ${e.exclude ? "#fecaca" : "#bbf7d0"}` }}>
          {e.exclude ? "Exclusion" : "Inclusion"}
        </span>
      )}
    </div>
    {e.selectAll
      ? <span style={{ fontSize: 11, color: "#1d4ed8", fontWeight: 600 }}>
    All Selected
  </span>
      : e.items.length > 0
        ? <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 5 }}>{e.items.map((item: string, i: number) => <span key={i} style={{ fontSize: 11, padding: "3px 10px", borderRadius: 9999, background: item.includes("· Inactive") ? "#d1d5db" : "#f1f5f9", color: "#334155", border: item.includes("· Inactive") ? "1px solid #94a3b8" : "1px solid #e2e8f0", fontWeight: 500 }}>{item}</span>)}</div>
        : <span style={{ color: "#cbd5e1", fontSize: 12, fontStyle: "italic" }}>No items</span>}
  </div>
))}
                  </div>
                </div>

                <div style={{ height: 1, background: "#e2e8f0", margin: "0 28px" }} />

                {/* Audit Trail */}
                <div style={{ padding: "20px 28px 8px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
                      <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: 12, color: "#16a34a" }} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Audit Trail</span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", letterSpacing: "0.02em" }}>Created By</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #3b82f6, #2563eb)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                          {String((selectedItem as any).created_by ?? "?")[0].toUpperCase()}
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{resolveUserName((selectedItem as any).created_by)}</span>
                      </div>
                    </div>
                    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", letterSpacing: "0.02em" }}>Created On</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                        <i className="fa-regular fa-calendar" style={{ fontSize: 14, color: "#3b82f6" }} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                          {(selectedItem as any).createdat ? formatDisplayDateTime((selectedItem as any).createdat) : "—"}
                        </span>
                      </div>
                    </div>
                    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", letterSpacing: "0.02em" }}>Last Updated</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                        <i className="fa-regular fa-clock" style={{ fontSize: 14, color: "#10b981" }} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                          {(selectedItem as any).updatedat ? formatDisplayDateTime((selectedItem as any).updatedat) : "—"}
                        </span>
                      </div>
                    </div>
                  </div>
              {Array.isArray(selectedItem.updated_by) && selectedItem.updated_by.length > 0 && (
  <div style={{ gridColumn: "1 / -1", background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", marginTop: 12 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
      <i className="fa-solid fa-pen-to-square" style={{ fontSize: 11, color: "#10b981" }} />
      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.02em" }}>Update History</span>
      <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#f0fdf4", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0", marginLeft: "auto" }}>
        {selectedItem.updated_by.length} update{selectedItem.updated_by.length !== 1 ? "s" : ""}
      </span>
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {((selectedItem as any).updated_by as { id?: number; user_id?: number; at?: string; date?: string; [key: string]: any }[]).slice().reverse().slice(0, showAllUpdates ? undefined : 4).map((entry, i) => {
        const updateDate = entry.date || entry.at || (entry as any).updatedat || (entry as any).timestamp || (i === 0 ? (selectedItem as any).updatedat : undefined);
        return (
        <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: i === 0 ? "#f0fdf4" : "#f8fafc", border: `1px solid ${i === 0 ? "#bbf7d0" : "#e2e8f0"}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: i === 0 ? "linear-gradient(135deg, #10b981, #059669)" : "linear-gradient(135deg, #94a3b8, #64748b)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
              {String(resolveUserName(entry.id ?? entry.user_id) ?? "?")[0].toUpperCase()}
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{resolveUserName(entry.id ?? entry.user_id)}</span>
            {i === 0 && <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#dcfce7", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0" }}>Latest</span>}
          </div>
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>
            {updateDate ? formatDisplayDateTime(updateDate) : "—"}
          </span>
        </div>
      );})}
      {selectedItem.updated_by.length > 4 && (
        <div style={{ textAlign: "center", padding: "6px 0" }}>
          <button onClick={() => setShowAllUpdates(v => !v)} style={{ fontSize: 11, color: "#3b82f6", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}>
            {showAllUpdates ? "Show less ↑" : `+${selectedItem.updated_by.length - 4} more updates ↓`}
          </button>
        </div>
      )}
    </div>
  </div>
)}
                </div>
              </div>

              <div style={{ padding: "14px 28px", borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, flexShrink: 0 }}>
                <Button onClick={() => setSelectedItem(null)} style={{ background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "#fff", fontWeight: 600, padding: "8px 28px", borderRadius: 10, boxShadow: "0 2px 8px rgba(37,99,235,0.3)" }}>
                  Close Details
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

  {/* ══ ADD / EDIT DIALOG ════════════════════════════════════════════════ */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-5xl h-[90vh] flex flex-col overflow-hidden p-0">
          <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold text-gray-900">
                {editIndex === null ? "Add Activity Bundle" : "Edit Activity Bundle"}
              </DialogTitle>
              </DialogHeader>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-0">
            {/* Section 1 */}
            <SectionCard num={1} title="Basic Information" subtitle="Name and status of the relation">
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <Label htmlFor="relation_name">Relation Name <span className="text-red-500 ml-0.5">*</span></Label>
                  <Input id="relation_name" value={relationName} onChange={(e) => setRelationName(e.target.value)} placeholder="Must be at least 3 characters" className="mt-1" />
                </div>
                <div className="w-48 flex-shrink-0">
                  <Label>Status</Label>
                  <div className="flex gap-2 mt-1">
                    <button type="button" onClick={() => setIsActive(true)} className={`flex-1 h-10 rounded-md text-sm font-medium border transition-all ${isActive ? "bg-green-50 text-green-800 border-green-300" : "bg-white text-gray-400 border-gray-200 hover:bg-gray-100"}`}>Active</button>
                    <button type="button" onClick={() => setIsActive(false)} className={`flex-1 h-10 rounded-md text-sm font-medium border transition-all ${!isActive ? "bg-red-50 text-red-800 border-red-300" : "bg-white text-gray-400 border-gray-200 hover:bg-gray-100"}`}>Inactive</button>
                  </div>
                </div>
              </div>
            </SectionCard>

            {/* Section 2 */}
            <SectionCard num={2} title="Activity configuration">
              <div className="space-y-5">
                <ActivitySourcePicker
                  title="Main Activity *"
                  selected={selectedMainOption}
                  onSelect={setSelectedMainOption}
                  codeOptionKey="list_of_Activity_code"
                  counts={{
                    code: mainActivitiesByType.CPT.length + mainActivitiesByType.HCPCS.length + mainActivitiesByType.Dental.length + mainActivitiesByType.Drugs.length,
                    medical: mainActivityMedicalGroups.length,
                    custom: mainActivityCustomGroups.length,
                  }}
                >
                  {selectedMainOption === "list_of_Activity_code" && (
                    <div className="space-y-3">
                      <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2"><i className="fa-solid fa-code text-blue-600" /> Main Activity Codes by Type</h3>
                      <div className="grid grid-cols-2 gap-3">
                        {(["CPT", "HCPCS", "Dental", "Drugs"] as const).map((key) => {
                          const bgMap: Record<string, string> = { CPT: "blue", HCPCS: "green", Dental: "purple", Drugs: "orange" };
                          const bg = bgMap[key];
                          return (
                            <div key={key} className={`bg-white p-3 rounded-lg border border-${bg}-200`}>
                              <Label className="flex items-center gap-2 mb-2">
                                <span className={`px-2 py-0.5 bg-${bg}-100 text-${bg}-900 rounded text-xs font-bold border border-${bg}-400`}>{key}</span>
                                <span className="text-xs text-gray-500">({mainActivitiesByType[key].length} codes)</span>
                              </Label>
                              <ListofActivityCode onChange={(selected: Option[]) => setMainActivitiesByType(prev => ({ ...prev, [key]: selected }))} type={key} defaultValue={mainActivitiesByType[key]} clear={false} isValid={true} />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {selectedMainOption === "medical_Activity_groups" && (
                    <ReferenceSelect values={mainActivityMedicalGroups} onChange={setMainActivityMedicalGroups}><ListOfActivityMedical onChange={setMainActivityMedicalGroups} defaultValue={mainActivityMedicalGroups} clear={clearMainActivityMedicalGroups} /></ReferenceSelect>
                  )}
                  {selectedMainOption === "custom_Activity_groups" && (
                    <ReferenceSelect values={mainActivityCustomGroups} onChange={setMainActivityCustomGroups}><ListOfActivityCustom onChange={setMainActivityCustomGroups} defaultValue={mainActivityCustomGroups} clear={clearMainActivityCustomGroups} /></ReferenceSelect>
                  )}
                </ActivitySourcePicker>

                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <Label className="block">Activities <span className="text-red-500 ml-0.5">*</span></Label>
                    
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={addActivityGroup}
                      className="shrink-0 border-blue-200 text-blue-700 hover:bg-blue-50"
                    >
                      <i className="fa-solid fa-plus mr-2 text-xs"></i>
                      Add Activities
                    </Button>
                  </div>

                  {activityGroups.map((group, groupIndex) => {
                    const codeCount =
                      group.activitiesByType.CPT.length +
                      group.activitiesByType.HCPCS.length +
                      group.activitiesByType.Dental.length +
                      group.activitiesByType.Drugs.length;

                    return (
                      <div
                        key={group.id}
                        className={`relative rounded-xl border border-slate-200 bg-white p-4 shadow-sm ${group.isExpanded ? "z-40" : "z-0"}`}
                      >
                        <div className={`flex items-center justify-between gap-3 ${group.isExpanded ? "mb-3" : ""}`}>
                          <button
                            type="button"
                            onClick={() => toggleActivityGroup(group.id)}
                            className="flex min-w-0 flex-1 items-center justify-between gap-3 rounded-lg px-1 py-1 text-left transition-colors hover:bg-slate-50"
                            aria-expanded={group.isExpanded}
                            aria-controls={`activity-group-content-${group.id}`}
                          >
                            <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-bold text-blue-700">
                              Activities {groupIndex + 1}
                            </span>

                            <span className="flex items-center gap-2 text-xs font-medium text-slate-500">
                              {!group.isExpanded && (
                                <span>
                                  {codeCount + group.medicalGroups.length + group.customGroups.length} selected
                                </span>
                              )}
                              <i
                                className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                                  group.isExpanded ? "rotate-180" : "rotate-0"
                                }`}
                              ></i>
                            </span>
                          </button>

                          {activityGroups.length > 1 && (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                removeActivityGroup(group.id);
                              }}
                              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-red-200 bg-red-50 text-red-500 transition-colors hover:bg-red-100"
                              title="Remove activities selection"
                              aria-label={`Remove Activities ${groupIndex + 1}`}
                            >
                              <i className="fa-solid fa-trash text-xs"></i>
                            </button>
                          )}
                        </div>

                        {group.isExpanded && (
                        <div id={`activity-group-content-${group.id}`}>
                        <ActivitySourcePicker
                          title=""
                          selected={group.selectedOption}
                          onSelect={(selectedOption) =>
                            updateActivityGroup(group.id, (current) => ({
                              ...current,
                              selectedOption,
                            }))
                          }
                          codeOptionKey="list_of_Activity"
                          counts={{
                            code: codeCount,
                            medical: group.medicalGroups.length,
                            custom: group.customGroups.length,
                          }}
                        >
                          {group.selectedOption === "list_of_Activity" && (
                            <div className="space-y-3">
                              <h3 className="flex items-center gap-2 text-sm font-bold text-gray-800">
                                <i className="fa-solid fa-code text-blue-600" />
                                Activities Codes by Type
                              </h3>
                              <div className="grid grid-cols-2 gap-3">
                                {(["CPT", "HCPCS", "Dental", "Drugs"] as const).map((key) => {
                                  const bgMap: Record<string, string> = {
                                    CPT: "blue",
                                    HCPCS: "green",
                                    Dental: "purple",
                                    Drugs: "orange",
                                  };
                                  const bg = bgMap[key];

                                  return (
                                    <div
                                      key={key}
                                      className={`bg-white p-3 rounded-lg border border-${bg}-200`}
                                    >
                                      <Label className="mb-2 flex items-center gap-2">
                                        <span className={`px-2 py-0.5 bg-${bg}-100 text-${bg}-900 rounded text-xs font-bold border border-${bg}-400`}>
                                          {key}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                          ({group.activitiesByType[key].length} codes)
                                        </span>
                                      </Label>
                                      <ListofActivityCode
                                        onChange={(selected: Option[]) =>
                                          updateActivityGroup(group.id, (current) => ({
                                            ...current,
                                            activitiesByType: {
                                              ...current.activitiesByType,
                                              [key]: filterDuplicateSelections(group.id, "code", selected, key),
                                            },
                                          }))
                                        }
                                        type={key}
                                        defaultValue={group.activitiesByType[key]}
                                        clear={false}
                                        isValid={true}
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {group.selectedOption === "medical_Activity_groups" && (
                            <ReferenceSelect values={group.medicalGroups} onChange={(selected: Option[]) => updateActivityGroup(group.id, current => ({ ...current, medicalGroups: preserveLocked(current.medicalGroups, selected) }))}>
                            <ListOfActivityMedical
                              onChange={(selected: Option[]) =>
                                updateActivityGroup(group.id, (current) => ({
                                  ...current,
                                  medicalGroups: preserveLocked(current.medicalGroups, filterDuplicateSelections(group.id, "medicalgroup", selected)),
                                }))
                              }
                              defaultValue={group.medicalGroups}
                              clear={false}
                            />
                            </ReferenceSelect>
                          )}

                          {group.selectedOption === "custom_Activity_groups" && (
                            <ReferenceSelect values={group.customGroups} onChange={(selected: Option[]) => updateActivityGroup(group.id, current => ({ ...current, customGroups: preserveLocked(current.customGroups, selected) }))}>
                            <ListOfActivityCustom
                              onChange={(selected: Option[]) =>
                                updateActivityGroup(group.id, (current) => ({
                                  ...current,
                                  customGroups: preserveLocked(current.customGroups, filterDuplicateSelections(group.id, "customgroup", selected)),
                                }))
                              }
                              defaultValue={group.customGroups}
                              clear={false}
                            />
                            </ReferenceSelect>
                          )}
                        </ActivitySourcePicker>
                        </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </SectionCard>

            {/* Section 3 – Entities */}
            <div className="relative z-0 border-2 border-blue-200 rounded-xl overflow-hidden mb-4 shadow-sm">
              <div className="flex items-center justify-between px-5 py-3 bg-blue-50 border-b border-blue-200">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-blue-700 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">3</span>
                  <div>
                    <p className="text-sm font-semibold text-blue-900">Entities Configuration</p>
                    <p className="text-xs text-blue-600">Scope and visibility — who this rule applies to</p>
                  </div>
                </div>
                <button type="button" onClick={handleToggleEntities} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${showEntities ? "bg-green-500" : "bg-gray-300"}`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${showEntities ? "translate-x-6" : "translate-x-1"}`} />
                </button>
              </div>

              {showEntities && (
                <div className="p-5 bg-blue-50">
                
                  <div className="grid grid-cols-2 gap-3">
                    <EntityCard lockedReferences={hasLocked(provider)} label="Providers" icon="fa-hospital" color="blue" isActive={isProviderActive} mode={providerMode}
                      onExclusion={() => { if (providerMode === "EXCLUSION") { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode(null); setIsProviderActive(false); } else { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode("EXCLUSION"); setIsProviderActive(true); } }}
                      onInclusion={() => { if (providerMode === "INCLUSION") { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode(null); setIsProviderActive(false); } else { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode("INCLUSION"); setIsProviderActive(true); } }}
                      onClear={() => { setProvider([]); setProviderMode(null); setIsProviderActive(false); setClearProvider(true); setSelectAllProviders(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllProviders} disabled={hasLocked(provider)} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllProviders(c); if (c) { setProvider([]); setClearProvider(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-blue-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Providers</span>
                      </label>
                      {!selectAllProviders && <ReferenceSelect values={provider} onChange={setProvider}><ListOfProviderName onChange={setProvider} defaultValue={provider} clear={clearProvider} /></ReferenceSelect>}
                    </EntityCard>

                    <EntityCard lockedReferences={hasLocked(doctor)} label="Doctor" icon="fa-stethoscope" color="cyan" isActive={isDoctorActive} mode={doctorMode}
                      onExclusion={() => { if (doctorMode === "EXCLUSION") { setDoctor([]); setClearDoctor(true); setSelectAllDoctor(false); setDoctorMode(null); setIsDoctorActive(false); } else { setDoctor([]); setClearDoctor(true); setSelectAllDoctor(false); setDoctorMode("EXCLUSION"); setIsDoctorActive(true); } }}
                      onInclusion={() => { if (doctorMode === "INCLUSION") { setDoctor([]); setClearDoctor(true); setSelectAllDoctor(false); setDoctorMode(null); setIsDoctorActive(false); } else { setDoctor([]); setClearDoctor(true); setSelectAllDoctor(false); setDoctorMode("INCLUSION"); setIsDoctorActive(true); } }}
                      onClear={() => { setDoctor([]); setDoctorMode(null); setIsDoctorActive(false); setClearDoctor(true); setSelectAllDoctor(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllDoctor} disabled={hasLocked(doctor)} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllDoctor(c); if (c) { setDoctor([]); setClearDoctor(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-cyan-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Doctors</span>
                      </label>
                      {!selectAllDoctor && <ReferenceSelect values={doctor} onChange={setDoctor}><ListOfDoctorName onChange={setDoctor} defaultValue={doctor} clear={clearDoctor} /></ReferenceSelect>}
                    </EntityCard>

                    <EntityCard lockedReferences={hasLocked(specialty)} label="Specialty" icon="fa-briefcase-medical" color="teal" isActive={isSpecialtyActive} mode={specialtyMode}
                      onExclusion={() => { if (specialtyMode === "EXCLUSION") { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode(null); setIsSpecialtyActive(false); } else { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode("EXCLUSION"); setIsSpecialtyActive(true); } }}
                      onInclusion={() => { if (specialtyMode === "INCLUSION") { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode(null); setIsSpecialtyActive(false); } else { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode("INCLUSION"); setIsSpecialtyActive(true); } }}
                      onClear={() => { setSpecialty([]); setSpecialtyMode(null); setIsSpecialtyActive(false); setClearSpecialty(true); setSelectAllSpecialty(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllSpecialty} disabled={hasLocked(specialty)} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllSpecialty(c); if (c) { setSpecialty([]); setClearSpecialty(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-teal-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Specialties</span>
                      </label>
                      {!selectAllSpecialty && <ReferenceSelect values={specialty} onChange={setSpecialty}><ListOfSpecialty onChange={setSpecialty} defaultValue={specialty} clear={clearSpecialty} /></ReferenceSelect>}
                    </EntityCard>

                    <EntityCard lockedReferences={hasLocked(networks)} label="Networks" icon="fa-network-wired" color="purple" isActive={isNetworkActive} mode={networkMode}
                      onExclusion={() => { if (networkMode === "EXCLUSION") { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode(null); setIsNetworkActive(false); } else { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode("EXCLUSION"); setIsNetworkActive(true); } }}
                      onInclusion={() => { if (networkMode === "INCLUSION") { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode(null); setIsNetworkActive(false); } else { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode("INCLUSION"); setIsNetworkActive(true); } }}
                      onClear={() => { setNetworks([]); setNetworkMode(null); setIsNetworkActive(false); setClearNetworks(true); setSelectAllNetworks(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllNetworks} disabled={hasLocked(networks)} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllNetworks(c); if (c) { setNetworks([]); setClearNetworks(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-purple-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Networks</span>
                      </label>
                      {!selectAllNetworks && <ReferenceSelect values={networks} onChange={setNetworks}><ListOfNewNetworkName onChange={setNetworks} defaultValue={networks} clear={clearNetworks} /></ReferenceSelect>}
                    </EntityCard>

                    <EntityCard lockedReferences={hasLocked(insuranceplan)} label="Insurance Plan" icon="fa-shield-halved" color="amber" isActive={isInsuranceActive} mode={insuranceMode}
                      onExclusion={() => { if (insuranceMode === "EXCLUSION") { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode(null); setIsInsuranceActive(false); } else { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode("EXCLUSION"); setIsInsuranceActive(true); } }}
                      onInclusion={() => { if (insuranceMode === "INCLUSION") { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode(null); setIsInsuranceActive(false); } else { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode("INCLUSION"); setIsInsuranceActive(true); } }}
                      onClear={() => { setInsuranceplan([]); setInsuranceMode(null); setIsInsuranceActive(false); setClearInsuranceplan(true); setSelectAllInsurance(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllInsurance} disabled={hasLocked(insuranceplan)} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllInsurance(c); if (c) { setInsuranceplan([]); setClearInsuranceplan(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-amber-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Insurance Plans</span>
                      </label>
                      {!selectAllInsurance && <ReferenceSelect values={insuranceplan} onChange={setInsuranceplan}><ListOfInsuranceplanName onChange={setInsuranceplan} defaultValue={insuranceplan} clear={clearInsuranceplan} /></ReferenceSelect>}
                    </EntityCard>

                    <EntityCard lockedReferences={hasLocked(policy)} label="Policy" icon="fa-file-lines" color="emerald" isActive={isPolicyActive} mode={policyMode}
                      onExclusion={() => { if (policyMode === "EXCLUSION") { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode(null); setIsPolicyActive(false); } else { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode("EXCLUSION"); setIsPolicyActive(true); } }}
                      onInclusion={() => { if (policyMode === "INCLUSION") { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode(null); setIsPolicyActive(false); } else { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode("INCLUSION"); setIsPolicyActive(true); } }}
                      onClear={() => { setPolicy([]); setPolicyMode(null); setIsPolicyActive(false); setClearPolicy(true); setSelectAllPolicy(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllPolicy} disabled={hasLocked(policy)} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllPolicy(c); if (c) { setPolicy([]); setClearPolicy(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-emerald-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Policies</span>
                      </label>
                      {!selectAllPolicy && <ReferenceSelect values={policy} onChange={(v: any[]) => setPolicy(v)}><PolicyNames onChange={(v: PolicyItem[]) => setPolicy(v)} defaultValue={policy} clear={clearPolicy} /></ReferenceSelect>}
                    </EntityCard>

                    <EntityCard lockedReferences={hasLocked(patients)} label="Patients" icon="fa-users" color="pink" isActive={isPatientActive} mode={patientMode}
                      onExclusion={() => { if (patientMode === "EXCLUSION") { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode(null); setIsPatientActive(false); } else { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode("EXCLUSION"); setIsPatientActive(true); } }}
                      onInclusion={() => { if (patientMode === "INCLUSION") { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode(null); setIsPatientActive(false); } else { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode("INCLUSION"); setIsPatientActive(true); } }}
                      onClear={() => { setPatients([]); setPatientMode(null); setIsPatientActive(false); setClearPatient(true); setSelectAllPatients(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllPatients} disabled={hasLocked(patients)} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllPatients(c); if (c) { setPatients([]); setClearPatient(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-pink-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Patients</span>
                      </label>
                      {!selectAllPatients && <ReferenceSelect values={patients} onChange={setPatients}><VipPatientSelector onChange={setPatients} defaultValue={patients} clear={clearPatient} /></ReferenceSelect>}
                    </EntityCard>

                    <EntityCard lockedReferences={hasLocked(vip)} label="VIP" icon="fa-star" color="yellow" isActive={isVipActive} mode={vipMode}
                      onExclusion={() => { if (vipMode === "EXCLUSION") { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode(null); setIsVipActive(false); } else { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode("EXCLUSION"); setIsVipActive(true); } }}
                      onInclusion={() => { if (vipMode === "INCLUSION") { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode(null); setIsVipActive(false); } else { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode("INCLUSION"); setIsVipActive(true); } }}
                      onClear={() => { setVip([]); setVipMode(null); setIsVipActive(false); setClearVip(true); setSelectAllVip(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllVip} disabled={hasLocked(vip)} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllVip(c); if (c) { setVip([]); setClearVip(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-yellow-600" />
                        <span className="text-sm font-medium text-slate-600">Select All VIP</span>
                      </label>
                      {!selectAllVip && <ReferenceSelect values={vip} onChange={setVip}><Vip onChange={setVip} defaultValue={vip} clear={clearVip} /></ReferenceSelect>}
                    </EntityCard>

                    <EntityCard label="Payer" icon="fa-building-columns" color="indigo" isActive={isPayerActive} mode={payerMode}
                      onExclusion={() => { if (payerMode === "EXCLUSION") { setPayer([]); setSelectAllPayer(false); setPayerMode(null); setIsPayerActive(false); } else { setPayer([]); setSelectAllPayer(false); setPayerMode("EXCLUSION"); setIsPayerActive(true); } }}
                      onInclusion={() => { if (payerMode === "INCLUSION") { setPayer([]); setSelectAllPayer(false); setPayerMode(null); setIsPayerActive(false); } else { setPayer([]); setSelectAllPayer(false); setPayerMode("INCLUSION"); setIsPayerActive(true); } }}
                      onClear={() => { setPayer([]); setPayerMode(null); setIsPayerActive(false); setSelectAllPayer(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllPayer} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllPayer(c); if (c) setPayer([]); }); }} className="w-4 h-4 rounded border-slate-300 accent-indigo-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Payers</span>
                      </label>
                      {!selectAllPayer && <Input value={Payer.join(",")} onChange={(e) => setPayer(e.target.value ? [e.target.value] : [])} placeholder="Enter Payer" />}
                    </EntityCard>

                    <EntityCard label="User Level" icon="fa-user-gear" color="slate" isActive={isUserActive} mode={userMode}
                      onExclusion={() => { if (userMode === "EXCLUSION") { setUserLevel([]); setSelectAllUser(false); setUserMode(null); setIsUserActive(false); } else { setUserLevel([]); setSelectAllUser(false); setUserMode("EXCLUSION"); setIsUserActive(true); } }}
                      onInclusion={() => { if (userMode === "INCLUSION") { setUserLevel([]); setSelectAllUser(false); setUserMode(null); setIsUserActive(false); } else { setUserLevel([]); setSelectAllUser(false); setUserMode("INCLUSION"); setIsUserActive(true); } }}
                      onClear={() => { setUserLevel([]); setUserMode(null); setIsUserActive(false); setSelectAllUser(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllUser} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllUser(c); if (c) setUserLevel([]); }); }} className="w-4 h-4 rounded border-slate-300 accent-slate-600" />
                        <span className="text-sm font-medium text-slate-600">Select All User Levels</span>
                      </label>
                      {!selectAllUser && <Input value={userLevel.join(",")} onChange={(e) => setUserLevel(e.target.value ? [e.target.value] : [])} placeholder="Enter User Level" />}
                    </EntityCard>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
            <Button variant="outline" onClick={() => { setEditIndex(null); clear(); setIsDialogOpen(false); }}>Cancel</Button>
            <Button onClick={() => editIndex === null ? addItem() : updateItem(editIndex)} className="bg-blue-700 hover:bg-blue-800 text-white">
              {editIndex === null ? "Add Relation " : "Update Relation"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ══ All Codes Dialog ══════════════════════════════════════════════════ */}
      <Dialog open={allCodesDialogOpen} onOpenChange={setAllCodesDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-blue-900 flex items-center gap-2">
              <i className="fa-solid fa-list"></i> {allCodesDialogTitle}
              <span className="ml-auto text-sm font-normal text-gray-600">({allCodesDialogItems.length + allCodesDialogGroups.length} items)</span>
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 overflow-y-auto max-h-[calc(85vh-140px)] pr-2">
            <div className="grid grid-cols-4 gap-3">
              {allCodesDialogItems.map((item, index) => {
                const colors = getActivityTypeColor(item.type);
                return (
                  <div key={index} className={`flex items-center justify-between gap-2 p-3 bg-white rounded-lg border hover:shadow-md transition-all ${colors.border}`}>
                    <span className={`font-bold text-base ${colors.text}`}>{item.code}</span>
                    <div className={`w-px h-6 ${colors.border}`}></div>
                    <span className={`text-xs font-bold uppercase ${colors.text} opacity-75`}>{item.type}</span>
                  </div>
                );
              })}
            </div>
            {allCodesDialogGroups.length > 0 && (
              <div className="mt-3 space-y-3">
                {allCodesDialogGroups.filter(g => g.startsWith("[Medical] ")).length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: "#16a34a", display: "inline-block" }} />
                      <span className="text-xs font-bold text-green-700">Medical Groups ({allCodesDialogGroups.filter(g => g.startsWith("[Medical] ")).length})</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {allCodesDialogGroups.filter(g => g.startsWith("[Medical] ")).map((g, idx) => (
                        <span key={idx} style={{ padding: "4px 12px", background: "#f0fdf4", color: "#15803d", borderRadius: 6, fontSize: 12, fontWeight: 600, border: "1px solid #86efac" }}>{g.replace("[Medical] ", "")}</span>
                      ))}
                    </div>
                  </div>
                )}
                {allCodesDialogGroups.filter(g => g.startsWith("[Custom] ")).length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: "#9333ea", display: "inline-block" }} />
                      <span className="text-xs font-bold text-purple-700">Custom Groups ({allCodesDialogGroups.filter(g => g.startsWith("[Custom] ")).length})</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {allCodesDialogGroups.filter(g => g.startsWith("[Custom] ")).map((g, idx) => (
                        <span key={idx} style={{ padding: "4px 12px", background: "#faf5ff", color: "#7e22ce", borderRadius: 6, fontSize: 12, fontWeight: 600, border: "1px solid #d8b4fe" }}>{g.replace("[Custom] ", "")}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex justify-end pt-4 border-t mt-4">
            <Button onClick={() => setAllCodesDialogOpen(false)} className="bg-blue-600 hover:bg-blue-700">Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ══ Permanent Delete Confirmation ═════════════════════════════════════ */}
      {forceDialogOpen && forceAction === "hardDelete" && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "#00000088",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              padding: 32,
              width: 440,
              boxShadow: "0 20px 50px rgba(0,0,0,0.3)",
              border: "1px solid #fecaca",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                gap: 12,
              }}
            >
              <div
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: "50%",
                  background: "#fef2f2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <i
                  className="fa-solid fa-triangle-exclamation"
                  style={{ fontSize: 28, color: "#dc2626" }}
                />
              </div>

              <h3
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#1e293b",
                  margin: 0,
                }}
              >
                Permanent Delete
              </h3>

              <p
                style={{
                  fontSize: 13,
                  color: "#64748b",
                  margin: 0,
                  lineHeight: 1.6,
                }}
              >
                This will permanently delete this Activity Bundle record.
                <br />
                This action cannot be undone.
              </p>

              {forceError && (
                <div
                  style={{
                    width: "100%",
                    background: "#fef9c3",
                    border: "1px solid #fde047",
                    borderRadius: 10,
                    padding: "12px 16px",
                    textAlign: "left",
                  }}
                >
                  <p
                    style={{
                      fontSize: 13,
                      color: "#713f12",
                      margin: 0,
                      lineHeight: 1.6,
                    }}
                  >
                    {forceError}
                  </p>
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  gap: 10,
                  width: "100%",
                  marginTop: 4,
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setForceDialogOpen(false);
                    setForceId(null);
                    setForceError("");
                    setForceAction(null);
                  }}
                  style={{
                    flex: 1,
                    background: "#f1f5f9",
                    color: "#475569",
                    border: "1px solid #e2e8f0",
                    borderRadius: 9,
                    padding: 12,
                    cursor: "pointer",
                    fontWeight: 600,
                    fontSize: 13,
                  }}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={() =>
                    forceId !== null && permanentDeleteItem(forceId)
                  }
                  style={{
                    flex: 1,
                    background: "#7f1d1d",
                    color: "#fff",
                    border: "none",
                    borderRadius: 9,
                    padding: 12,
                    cursor: "pointer",
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  <i
                    className="fa-solid fa-trash"
                    style={{ marginRight: 6 }}
                  />
                  Delete Permanently
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Toaster />
    </div>
  );
};

export default ActivityNotAllowedIncludeActivity;
